/* eslint-disable arrow-body-style */
import React, { useRef, useMemo, useCallback, useState, useEffect } from 'react'

import { protectedRoutes } from 'constants/routes'

import AutorenewIcon from '@mui/icons-material/Autorenew';
import BlockIcon from '@mui/icons-material/Block';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import QueryStatsIcon from '@mui/icons-material/QueryStats'
import { AgGridReact, useGridMenuItem } from 'ag-grid-react'
import clsx from 'clsx'
import RatingRenderer from 'components/commonparts/GridCustomField/RatingRenderer'
import { LoadingOverlay } from 'components/commonparts/index'
import StockCellEditor from 'components/parts/StockCenter/StockGrid/StockCellEditor'
import { format } from 'date-fns'
import { ja } from 'date-fns/locale'
import { useGridBase } from 'hooks/useGridBase'
import { computeRenderAwareSelectionStats, type GridSelectionSummaryStats } from 'hooks/useGridEdit'
import { type SimpleDataListPropsBase } from 'types/dataGridTypes'
import { isEqualDate, normalizeDate } from 'utils/convertFromLocalDate'
import { getDisplayDaylistFromCenterSupplier } from 'utils/stockDaylist'

import type { ColumnMenuTab, Column, FindPart, GridApi, ICellRendererParams } from 'ag-grid-community'
import type {
  ColDef,
  ColGroupDef,
  ValueFormatterParams,
  ValueParserParams,
  EditableCallbackParams,
  CellValueChangedEvent,
  CellClickedEvent,
  CellClassParams,
  IAggFuncParams,
  ICellEditorParams,
  IRowNode,
  GridReadyEvent,
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GetMainMenuItemsParams,
  HeaderValueGetterParams,
  KeyCreatorParams,
  MenuItemDef,
  ModelUpdatedEvent,
  ValueGetterParams,
  IsGroupOpenByDefaultParams,
  RowGroupOpenedEvent,
} from 'ag-grid-enterprise'
import type { CustomMenuItemProps } from 'ag-grid-react'
import type { Stocks, Daylist, Stock } from 'api-client'
import type { StockRow, StockCell } from 'types/table-model2'

import 'ag-grid-enterprise'
import 'ag-grid-community/styles/ag-grid.css'
import 'utils/mst.scss'

type Lv3CheckboxMenuItemParams = {
  checked: boolean
  getChecked?: () => boolean
  onCheckedChange: (checked: boolean) => void
}

/** グループを開く submenu: HTML checkbox + label (AG Grid `checked` is tick only). */
function Lv3CheckboxMenuItem({ menuItemParams, name }: CustomMenuItemProps<StockRow>) {
  const params = menuItemParams as Lv3CheckboxMenuItemParams | undefined
  const readChecked = useCallback(() => (
    typeof params?.getChecked === 'function'
      ? params.getChecked()
      : (params?.checked ?? false)
  ), [params])
  const [checked, setChecked] = useState(readChecked)

  useEffect(() => {
    // Keep in sync when menu is reopened / state changed externally.
    setChecked(readChecked())
  }, [readChecked, name])

  useGridMenuItem({
    configureDefaults: () => ({
      suppressClick: true,
      suppressMouseDown: true,
    }),
  })

  return (
    <div className="ag-menu-option" style={{ display: 'table-row', width: '100%' }} role="presentation">
      <span className="ag-menu-option-part ag-menu-option-icon" style={{ display: 'table-cell', verticalAlign: 'middle', width: 36 }}>
        <input
          type="checkbox"
          aria-label={String(name ?? '')}
          checked={checked}
          onChange={(e) => {
            e.stopPropagation()
            const next = e.target.checked
            // Optimistic UI: reflect click immediately, then apply heavy grid state update.
            setChecked(next)
            setTimeout(() => {
              params?.onCheckedChange(next)
            }, 0)
          }}
          onClick={(e) => e.stopPropagation()}
        />
      </span>
      <span className="ag-menu-option-part ag-menu-option-text" style={{ display: 'table-cell' }}>
        {name}
      </span>
      <span className="ag-menu-option-part ag-menu-option-shortcut" style={{ display: 'table-cell' }} />
      <span className="ag-menu-option-part ag-menu-option-popup-pointer" style={{ display: 'table-cell' }} />
    </div>
  )
}

interface DataListProps extends SimpleDataListPropsBase {
  data: Stocks
  rowData: StockRow[]
  unitType: 'case' | 'pallet'
  systemDate?: Date
  startDateTypeCode?: number
  endDateTypeCode?: number
  isUpdating?: boolean
  gridRef?: React.RefObject<AgGridReact>
  findSearchValue?: string
  onFindChanged?: (event: any) => void
  isSearchVisible?: boolean
  onCellValueChanged?: (event: CellValueChangedEvent<StockRow>) => void
  onModelUpdated?: (event: ModelUpdatedEvent<StockRow>) => void
  tableEditable?: boolean
  onRenewalClick?: (renewalFlg: boolean, centerId: string, itemCode: string) => void
  onInfoClick?: (centerId: number, itemCode: string) => void
  /** 検索・初期化(reload)のたびに増やすと、日付/項目/autoGroup のメニュー状態と列フィルターをクリアする */
  gridMenuResetSeq?: number
}

type SortConfig = {
  colId: string
  sortBy: 'arrival' | 'ship' | 'salesorder'
  direction: 'asc' | 'desc'
}

const GROUP_PANEL_LABELS: Record<string, string> = {
  supplierName: '仕入先',
  itemName: '商品',
  centerName: 'センター',
  'rowType.group': 'グループ',
}

const GROUP_PANEL_SORT_FIELDS = ['supplierName', 'itemName', 'centerName', 'rowType.group'] as const
const TOP_LEVEL_GROUP_FIELDS = ['supplierName', 'itemName', 'centerName'] as const
const DEFAULT_NON_ROWTYPE_GROUP_ORDER = ['supplierName', 'itemName', 'centerName'] as const
const ROW_GROUP_ORDER_STORAGE_KEY = 'stock-center2-row-group-order'
const DEFAULT_GROUP_EXPANDED_LEVEL = 3
/** 項目列メニュー展開 state のシグネチャ（default 判定用） */
const DEFAULT_MENU_EXPANSION_SIG = 'default||0'
const GROUP_EXPANSION_DEBOUNCE_MS = 48
type GroupDisplayMode = 'default' | 'expanded' | 'collapsed'

function buildMenuExpansionSig(
  groupDisplayMode: GroupDisplayMode,
  openedRowTypeGroupsKey: string,
  isGroupDisplayDirty: boolean
): string {
  return `${groupDisplayMode}|${openedRowTypeGroupsKey}|${isGroupDisplayDirty ? 1 : 0}`
}

function isAlertColIdActive(
  colId: string | null | undefined,
  daylist: ReadonlyArray<Daylist>
): boolean {
  if (!colId) {
    return false
  }
  const day = daylist.find((d) => d.colname === colId)
  return !!day && ['today', 'expected'].includes(day.rectype ?? '')
}

function sampleRowMatchesDisplayFocus(
  sample: StockRow,
  focusKey: ItemDisplayFocusKey
): boolean {
  if (focusKey === 'expectedAlert' && sample.isExpectedRankAlert === true) {
    return true
  }
  if (focusKey === 'renewal' && sample.renewalFlg === true) {
    return true
  }
  if (focusKey === 'newItem' && (sample as { isMinShipDateAlert?: boolean }).isMinShipDateAlert === true) {
    return true
  }
  if (focusKey === 'endSale' && (sample as { isEndSaleAlert?: boolean }).isEndSaleAlert === true) {
    return true
  }
  return false
}

function displayFilterSig(filter: DisplayFilter | null): string {
  if (!filter) {
    return ''
  }
  return filter.kind === 'item' ? `item:${filter.key}` : `alert:${filter.colId}`
}

function beforeColId(col: string): string {
  const index = parseInt(col.replace('col', ''), 10)
  return `col${index - 1}`
}

function findProductRowByType(rows: StockRow[], rowTypeCode: string): StockRow | undefined {
  return rows.find((r) => r.rowType?.code === rowTypeCode && r.isTotal !== true)
}

/** 日付列 alert_hight 判定（同一商品の rowType 行集合から判定） */
function productRowsHaveAlertForCol(
  rows: StockRow[],
  colId: string,
  daylist: ReadonlyArray<Daylist>
): boolean {
  const day = daylist.find((d) => d.colname === colId)
  if (!day || !['today', 'expected'].includes(day.rectype ?? '')) {
    return false
  }

  const prevColId = beforeColId(colId)
  const stockRow = findProductRowByType(rows, 'stock_quantity')
  const avgRow = findProductRowByType(rows, 'avgShipQuantityStockBase')
  const stockDaysRow = findProductRowByType(rows, 'stockDays')
  const shipRow = findProductRowByType(rows, 'shipQuantity')

  const stockCell = stockRow?.[colId as keyof StockRow] as StockCell | null | undefined
  const avgCell = avgRow?.[colId as keyof StockRow] as StockCell | null | undefined
  const stockDaysCell = stockDaysRow?.[colId as keyof StockRow] as StockCell | null | undefined
  const shipCell = shipRow?.[colId as keyof StockRow] as StockCell | null | undefined

  if (
    stockCell?.value != null &&
    Number(stockCell.value) <= 0 &&
    avgCell?.value != null &&
    Number(avgCell.value) > 0
  ) {
    return true
  }
  if (
    stockDaysCell?.value != null &&
    stockDaysCell.shipdaysNextArrival != null &&
    Number(stockDaysCell.value) <= Number(stockDaysCell.shipdaysNextArrival)
  ) {
    return true
  }
  if (shipCell?.value != null && Number(shipCell.value) > 0 && prevColId) {
    const prevStockCell = stockRow?.[prevColId as keyof StockRow] as StockCell | null | undefined
    if (
      prevStockCell?.value != null &&
      Number(shipCell.value) >= Number(prevStockCell.value)
    ) {
      return true
    }
  }
  return false
}

const DUPLICATE_LEVEL4_ROWTYPE_CODES = new Set([
  'arrivalQuantity',
  'shipQuantity',
  'stock_quantity',
  'stockQuantityRec',
  'stockDays',
])
type ItemDisplayFocusKey = 'expectedAlert' | 'renewal' | 'newItem' | 'endSale'
/** 表示フィルター：項目列・日付列とも同時に 1 つのみ（排他）。 */
type DisplayFilter =
  | { kind: 'item'; key: ItemDisplayFocusKey }
  | { kind: 'alert'; colId: string }
/** 上に表示：排他ソート（同時に 1 つのみ）。 */
type ItemSortTopKey = 'renewalTop' | 'newItemTop' | 'endSaleTop'
type ActiveProductSortKind = 'none' | 'expectedRank' | ItemSortTopKey | 'alertTop'
const LV3_ITEM_GROUPS = ['入荷', '出荷', '在庫数', '在庫日数', '備考'] as const

function createAllLv3GroupsSet(): Set<string> {
  return new Set<string>(LV3_ITEM_GROUPS as unknown as string[])
}

/** Immutable template — never mutate; copy before in-place edits. */
const ALL_LV3_GROUPS_SET = createAllLv3GroupsSet()
const ALL_LV3_GROUPS_VISIBLE_SET = createAllLv3GroupsSet()

function isSameDisplayFilter(
  current: DisplayFilter | null,
  next: DisplayFilter
): boolean {
  if (!current) {
    return false
  }
  if (current.kind !== next.kind) {
    return false
  }
  if (current.kind === 'item') {
    return next.kind === 'item' && current.key === next.key
  }
  return next.kind === 'alert' && current.colId === next.colId
}

const ROW_TYPE_GROUP_SORT_ORDER: Record<string, number> = {
  入荷: 1,
  出荷: 2,
  在庫数: 3,
  在庫日数: 4,
  備考: 5,
}

const HEADER_GROUP_REC = 'headerGroupRec'
const HEADER_GROUP_TODAY = 'headerGroupToday'
const HEADER_GROUP_EXPECTED = 'headerGroupExpected'
const SELECTION_SUMMARY_FOOTER_STORAGE_KEY = 'stock-center-footer'

const SUBTOTAL_ROW_TYPE_ORDER = [
  'arrivalQuantity',
  'orderQuantityRec',
  'orderQuantityRecommend',
  'arrivalQuantityPlan',
  'orderQuantity',
  'shipQuantity',
  'shipQuantityExpected',
  'salesorderQuantity',
  'shipQuantityPlan',
  'stock_quantity',
  'stockQuantityRec',
  'calcSafety',
  'stockQuantityShipStop',
  'stockDays',
  'avgShipQuantityStockBase',
] as const

/**
 * 出荷予測精度で並び替え: today の prediction_mape_median 降順（★1→2→3→0 相当）。
 * null / undefined / 不正値は常に最後。
 */
function comparePredictionMapeMedianForSort(
  aMape: number | null | undefined,
  bMape: number | null | undefined
): number {
  const aMissing = aMape == null || Number.isNaN(Number(aMape))
  const bMissing = bMape == null || Number.isNaN(Number(bMape))
  if (aMissing && bMissing) {
    return 0
  }
  if (aMissing) {
    return 1
  }
  if (bMissing) {
    return -1
  }
  const av = Number(aMape)
  const bv = Number(bMape)
  if (av !== bv) {
    return bv - av
  }
  return 0
}

/**
 * 合計行で素朴な Σmax(0,..) が意味を持たない rowType。
 * 値は親側の calcTotal が再計算で上書きするため、初期表示は null に揃える。
 */
const NON_SUMMABLE_TOTAL_ROW_TYPES: ReadonlySet<string> = new Set([
  'stockDays',
  'avgShipQuantityStockBase',
])

/** Levels 0–2 open by default; level 3+ (入荷, 出荷…) closed until user expands. */
/**
 * Stable group path from row-group column + key (keyCreator output).
 * Do not use node.id — AG Grid regenerates internal ids on refreshCells / model refresh.
 */
function buildStockGroupNodePath(node: IRowNode<StockRow>): string {
  const segments: string[] = []
  let current: IRowNode<StockRow> | null = node
  while (current) {
    if (current.group) {
      const colId = current.rowGroupColumn?.getColId() ?? `level${current.level ?? 0}`
      segments.unshift(`${colId}:${String(current.key ?? '')}`)
    }
    current = current.parent
  }
  return segments.join('|')
}

function shouldStockGroupBeOpenByDefault(
  node: IRowNode<StockRow>,
  userExpandedKeys: ReadonlySet<string>
): boolean {
  const path = buildStockGroupNodePath(node)
  if (path && userExpandedKeys.has(path)) {
    return true
  }
  return (node.level ?? 0) < DEFAULT_GROUP_EXPANDED_LEVEL
}

function calculateItemColumnWidth(rows: StockRow[]): number {
  const BASE_WIDTH = 165
  const ICON_STEP = 30
  const MAX_WIDTH = 340
  const MAX_ALERT_ICON_COUNT = 3

  const groupAlertStateMap = new Map<string, { renewal: boolean; minShip: boolean; endSale: boolean; count: number }>()
  let maxVisibleAlertIcons = 1

  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i]
    const key = row.groupKey ?? `${row.centerId ?? ''}|${row.orgSupplierId ?? ''}|${row.itemCode ?? ''}`
    const state = groupAlertStateMap.get(key) ?? {
      renewal: false,
      minShip: false,
      endSale: false,
      count: 0,
    }
    let changed = false

    if (!state.renewal && row.renewalFlg === true) {
      state.renewal = true
      state.count += 1
      changed = true
    }
    if (!state.minShip && (row as any).isMinShipDateAlert === true) {
      state.minShip = true
      state.count += 1
      changed = true
    }
    if (!state.endSale && (row as any).isEndSaleAlert === true) {
      state.endSale = true
      state.count += 1
      changed = true
    }

    if (changed) {
      groupAlertStateMap.set(key, state)
      const visibleIcons = state.count > 0 ? state.count : 1

      if (visibleIcons > maxVisibleAlertIcons) {
        maxVisibleAlertIcons = visibleIcons
        if (maxVisibleAlertIcons >= MAX_ALERT_ICON_COUNT) {
          break
        }
      }
    }
  }

  const calculated = BASE_WIDTH + (maxVisibleAlertIcons - 1) * ICON_STEP
  return Math.min(MAX_WIDTH, calculated)
}

function isTotalOnlyGroupNode(node: IRowNode<StockRow> | null | undefined): boolean {
  if (!node?.group) {
    return false
  }
  const leaves = node.allLeafChildren ?? []
  return leaves.length > 0 && leaves.every((child) => child?.data?.isTotal === true)
}

/** センター合計 / 仕入先合計 / 商品合計 など：優先ソート対象外 */
function isStockRowSubtotalOrTotal(row: StockRow | undefined): boolean {
  if (!row) {
    return false
  }
  if (row.isTotal === true) {
    return true
  }
  const gk = row.groupKey
  if (typeof gk === 'string' && gk.startsWith('subtotal__')) {
    return true
  }
  const ic = row.itemCode
  if (typeof ic === 'string' && ic.startsWith('subtotal__')) {
    return true
  }
  return false
}

type ProductGroupSortEntry = {
  rows: StockRow[]
  index: number
  sample: StockRow
}

function productSortTopTier(sample: StockRow | undefined, kind: ItemSortTopKey): number {
  if (!sample || isStockRowSubtotalOrTotal(sample)) {
    return 99
  }
  if (kind === 'renewalTop' && sample.renewalFlg === true) {
    return 0
  }
  if (kind === 'newItemTop' && (sample as { isMinShipDateAlert?: boolean }).isMinShipDateAlert === true) {
    return 0
  }
  if (kind === 'endSaleTop' && (sample as { isEndSaleAlert?: boolean }).isEndSaleAlert === true) {
    return 0
  }
  return 99
}

function compareProductGroupSortEntries(
  a: ProductGroupSortEntry,
  b: ProductGroupSortEntry,
  kind: ActiveProductSortKind,
  alertColId: string | null,
  alertFlagByGroupKey: ReadonlyMap<string, boolean> | null
): number {
  if (kind === 'expectedRank') {
    const cmp = comparePredictionMapeMedianForSort(
      a.sample.predictionMapeMedian,
      b.sample.predictionMapeMedian
    )
    if (cmp !== 0) {
      return cmp
    }
  } else if (kind === 'renewalTop' || kind === 'newItemTop' || kind === 'endSaleTop') {
    const va = productSortTopTier(a.sample, kind)
    const vb = productSortTopTier(b.sample, kind)
    if (va !== vb) {
      return va - vb
    }
  } else if (kind === 'alertTop' && alertColId && alertFlagByGroupKey) {
    const va = alertFlagByGroupKey.get(String(a.sample.groupKey ?? '')) ? 0 : 99
    const vb = alertFlagByGroupKey.get(String(b.sample.groupKey ?? '')) ? 0 : 99
    if (va !== vb) {
      return va - vb
    }
  }
  return a.index - b.index
}

const SUBTOTAL_GROUP_KEY_PATTERN = /^subtotal__(supplierName|itemName|centerName)__(.+)$/
const TOP_FIELD_TO_COLUMN_KIND: Record<'supplierName' | 'itemName' | 'centerName', 'supplier' | 'item' | 'center'> = {
  supplierName: 'supplier',
  itemName: 'item',
  centerName: 'center',
}

function resolveSubtotalGroupKey(
  data: StockRow | null | undefined,
  columnKind: 'supplier' | 'item' | 'center'
): string | null {
  if (!data?.isTotal) {
    return null
  }
  const match = SUBTOTAL_GROUP_KEY_PATTERN.exec(data.groupKey ?? '')
  if (!match) {
    return null
  }
  const topField = match[1] as 'supplierName' | 'itemName' | 'centerName'
  const topKey = match[2]
  const topKind = TOP_FIELD_TO_COLUMN_KIND[topField]
  if (topKind === columnKind) {
    return topKey
  }
  return `subtotal__${columnKind}__${topField}__${topKey}`
}

/**
 * key:        rowGrouping 用の安定識別子（leaf の keyCreator と一致させる必要があるため不変）。
 * label:      表示用ラベル（itemName / supplierName / centerName）。
 * displayCode: subtotal 行の表示で `[code]label` の code 部分に使う値。
 *              itemName の場合のみ orgItemCode を優先（業務識別子）。
 */
function getTopGroupMeta(row: StockRow, topGroupField: string): { key: string; label: string; displayCode: string } {
  switch (topGroupField) {
    case 'supplierName': {
      const key = row.orgSupplierId != null && row.orgSupplierId !== '' ? String(row.orgSupplierId) : 'unknown_supplier'
      const label = row.supplierName ?? row.orgSupplierId ?? '未設定'
      return { key: String(key), label: String(label), displayCode: key }
    }
    case 'itemName': {
      const key = row.itemCode != null && row.itemCode !== '' ? String(row.itemCode) : 'unknown_item'
      const label = row.itemName ?? row.itemCode ?? row.orgItemCode ?? '未設定'
      const displayCode =
        row.orgItemCode != null && row.orgItemCode !== '' ? String(row.orgItemCode) : key
      return { key: String(key), label: String(label), displayCode }
    }
    case 'centerName': {
      const key = row.centerId != null ? String(row.centerId) : 'unknown_center'
      const label = row.centerName ?? (row.centerId != null ? String(row.centerId) : '未設定')
      return { key: String(key), label: String(label), displayCode: key }
    }
    default:
      return { key: '', label: '', displayCode: '' }
  }
}

function formatTopGroupCodeName(topGroupField: string, key: string, label: string): string {
  if (!key || key.startsWith('unknown_')) {
    return label
  }

  if (!label) {
    return `[${key}]`
  }

  if (topGroupField === 'centerName') {
    return `[${key}]${label}`
  }
  if (topGroupField === 'supplierName') {
    return `[${key}]${label}`
  }
  if (topGroupField === 'itemName') {
    return `[${key}]${label}`
  }

  return label
}

function buildSubtotalRowsByTopGroup(rows: StockRow[], topGroupField: string): StockRow[] {
  if (!TOP_LEVEL_GROUP_FIELDS.includes(topGroupField as any)) {
    return rows.filter((row) => !row.isTotal)
  }

  const dataRows = rows.filter((row) => !row.isTotal)
  if (dataRows.length === 0) {
    return []
  }

  const grouped = new Map<string, { label: string; displayCode: string; rows: StockRow[] }>()
  dataRows.forEach((row) => {
    const { key, label, displayCode } = getTopGroupMeta(row, topGroupField)
    if (!grouped.has(key)) {
      grouped.set(key, { label, displayCode, rows: [] })
    }
    grouped.get(key)!.rows.push(row)
  })

  const subtotalRows: StockRow[] = []
  grouped.forEach(({ rows: groupRows, label: groupLabel, displayCode }, groupKey) => {
    const rowTypeMap = new Map<string, StockRow[]>()
    groupRows.forEach((row) => {
      const code = row.rowType?.code
      if (!code) {
        return
      }
      if (!rowTypeMap.has(code)) {
        rowTypeMap.set(code, [])
      }
      rowTypeMap.get(code)!.push(row)
    })

    const colKeys = Object.keys(groupRows[0] ?? {}).filter((key) => /^col\d+$/.test(key))
    SUBTOTAL_ROW_TYPE_ORDER.forEach((rowTypeCode) => {
      const sourceRows = rowTypeMap.get(rowTypeCode)
      if (!sourceRows || sourceRows.length === 0) {
        return
      }

      const template = sourceRows[0]
      let totalName = 'センター合計'
      if (topGroupField === 'itemName') {
        totalName = '商品合計'
      } else if (topGroupField === 'supplierName') {
        totalName = '仕入先合計'
      }
      const subtotalRow: StockRow = {
        ...template,
        isTotal: true,
        groupKey: `subtotal__${topGroupField}__${groupKey}`,
        orgItemCode: formatTopGroupCodeName(topGroupField, displayCode, groupLabel || ''),
        itemCode: `subtotal__${topGroupField}__${groupKey}`,
        itemName: topGroupField === 'itemName' ? groupLabel : totalName,
        supplierName: topGroupField === 'supplierName' ? groupLabel : totalName,
        centerName: topGroupField === 'centerName' ? groupLabel : totalName,
      }

      const isNonSummable = NON_SUMMABLE_TOTAL_ROW_TYPES.has(rowTypeCode)

      colKeys.forEach((colKey) => {
        const templateCell = template[colKey as keyof StockRow] as StockCell | null
        if (!templateCell) {
          ;(subtotalRow as any)[colKey] = null
          return
        }

        if (isNonSummable) {
          ;(subtotalRow as any)[colKey] = {
            ...templateCell,
            value: null,
            enableUpdate: false,
            isUpdate: false,
            isCurrentUpdate: false,
          }
          return
        }

        const sum = sourceRows.reduce((acc, row) => {
          const cell = row[colKey as keyof StockRow] as StockCell | null
          if (!cell || cell.value == null || Number.isNaN(Number(cell.value))) {
            return acc
          }
          return acc + Math.max(Number(cell.value), 0)
        }, 0)

        ;(subtotalRow as any)[colKey] = {
          ...templateCell,
          value: sum,
          enableUpdate: false,
          isUpdate: false,
          isCurrentUpdate: false,
        }
      })

      subtotalRows.push(subtotalRow)
    })
  })

  // 合計行をデータ行より常に先（上）に出す
  return [...subtotalRows, ...dataRows]
}

/**
 * Find 用テキスト（`itemColumnDisplayValueGetter` と一致）。
 * `null` のセルは Find 対象外（出荷グループは RatingRenderer のみ）。
 */
function getItemColumnFindText(
  node: IRowNode<StockRow> | null | undefined,
  data: StockRow | undefined
): string | null {
  if (!node) {
    return null
  }
  if (node.group && node.level === 2) {
    const v = node.allLeafChildren?.[0]?.data?.orgItemCode
    return v != null ? String(v) : ''
  }
  if (node.group && node.level === 3 && node.key === '出荷') {
    return null
  }
  if (node.group && node.level === 3) {
    return ''
  }
  return data?.rowType?.name != null ? String(data.rowType.name) : ''
}

function useRerenderOnGridFindChanged(api: GridApi<StockRow> | undefined) {
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!api || api.isDestroyed()) {
      return undefined
    }
    const onFindChanged = () => setTick((t) => t + 1)
    api.addEventListener('findChanged', onFindChanged)
    return () => {
      if (!api.isDestroyed()) {
        api.removeEventListener('findChanged', onFindChanged)
      }
    }
  }, [api])
}

function renderItemColumnLabelWithFind(
  api: GridApi<StockRow> | undefined,
  column: Column | undefined,
  node: IRowNode<StockRow>,
  displayText: string
): React.ReactNode {
  if (!displayText || !api || api.isDestroyed() || !column) {
    return displayText
  }
  const parts: FindPart[] = api.findGetParts({
    value: displayText,
    node,
    column,
  })
  let charOffset = 0
  return parts.map((part) => {
    const key = `find-${charOffset}-${part.match ? '1' : '0'}-${part.activeMatch ? '1' : '0'}`
    charOffset += part.value.length
    if (part.match) {
      return (
        <mark
          key={key}
          className={part.activeMatch ? 'ag-find-match ag-find-active-match' : 'ag-find-match'}
        >
          {part.value}
        </mark>
      )
    }
    return <span key={key}>{part.value}</span>
  })
}

function ActionCellRenderer(params: ICellRendererParams<StockRow> & { dataValue?: StockRow[] }) {
  const { node, value, dataValue, api, column } = params
  useRerenderOnGridFindChanged(api)

  const hasAlertMaster = dataValue?.some((dt) => (dt as any).isAlertMaster === true)
  const renewalF = dataValue?.some((dt) => dt.renewalFlg === true)
  const hasMinShipDateAlert = dataValue?.some((dt) => (dt as any).isMinShipDateAlert === true)
  const hasEndSaleAlert = dataValue?.some((dt) => (dt as any).isEndSaleAlert === true)

  if (node?.group && node.level === 2 && value) {
    const isTotalGroup = node.allLeafChildren?.every((child) => child?.data?.isTotal === true)
    const label = renderItemColumnLabelWithFind(api, column ?? undefined, node, String(value))
    if (isTotalGroup) {
      return <span>{label}</span>
    }
    return (
      <div className="stock-grid-action-cell">
        <span>{label}</span>
        <InfoOutlinedIcon
          className={clsx('action-icon-info', { has_alert: hasAlertMaster })}
          color="inherit"
          fontSize="small"
        />
        <QueryStatsIcon
          className="action-icon-chart"
          color="inherit"
          fontSize="small"
        />
        {renewalF && (
          <AutorenewIcon
            className="action-icon-renewal is-active"
            color="inherit"
            fontSize="small"
            data-has-renewal={renewalF}
          />
        )}
        {!renewalF && !hasMinShipDateAlert && !hasEndSaleAlert && (
          <HorizontalRuleIcon
            className={clsx('icon', { has_alert: hasMinShipDateAlert })}
            color="inherit"
            fontSize="small"
          />
        )}
        {hasEndSaleAlert && (
          <BlockIcon
            className={clsx('icon has_alert', { has_alert: hasEndSaleAlert })}
            color="inherit"
            fontSize="small"
          />
        )}
        {hasMinShipDateAlert && (
          <FiberNewIcon
            className={clsx('icon has_alert', { has_alert: hasMinShipDateAlert })}
            color="inherit"
            fontSize="small"
          />
        )}

      </div>
    )
  }

  if (node?.group && node.level === 3 && node.key === '出荷') {
    const isTotalGroup = node.allLeafChildren?.every((child) => child?.data?.isTotal === true)
    if (isTotalGroup) {
      return null
    }
    const expectedRank = node.allLeafChildren?.[0]?.data?.expectedRank
    // @ts-expect-error ag-grid component props mismatch expected when not mounted via ag-grid directly
    return <RatingRenderer value={expectedRank as any} node={node as any} />
  }

  if (value == null || value === '') {
    return null
  }
  const label = node
    ? renderItemColumnLabelWithFind(api, column ?? undefined, node, String(value))
    : String(value)
  return <span>{label}</span>
}

function RenderAwareAggregationStatusPanel(props: { api: GridApi<StockRow> }) {
  const { api } = props
  const [stats, setStats] = useState<GridSelectionSummaryStats | null>(null)

  useEffect(() => {
    if (!api || api.isDestroyed()) {
      return undefined
    }

    let rafId: number | null = null
    const recompute = () => {
      if (rafId != null) {
        window.cancelAnimationFrame(rafId)
      }
      rafId = window.requestAnimationFrame(() => {
        setStats(computeRenderAwareSelectionStats(api))
      })
    }

    recompute()
    api.addEventListener('rangeSelectionChanged' as any, recompute)
    api.addEventListener('cellSelectionChanged' as any, recompute)

    return () => {
      if (rafId != null) {
        window.cancelAnimationFrame(rafId)
      }
      if (!api.isDestroyed()) {
        api.removeEventListener('rangeSelectionChanged' as any, recompute)
        api.removeEventListener('cellSelectionChanged' as any, recompute)
      }
    }
  }, [api])

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', columnGap: 24 }}>
      {stats ? (
        <>
          <span>選択範囲のサマリ:</span>
          <span>選択数: {stats.count.toLocaleString()}</span>
          <span>合計: {stats.sum.toLocaleString()}</span>
          <span>平均値: {stats.avg.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
          <span>最小値: {stats.min.toLocaleString()}</span>
          <span>最大値: {stats.max.toLocaleString()}</span>
        </>
      ) : (
        <>
          <span>選択範囲のサマリ:</span>
          <span>セル範囲を選択してください</span>
        </>
      )}
    </span>
  )
}

function DataList({
  data,
  rowData,
  unitType,
  systemDate,
  startDateTypeCode,
  endDateTypeCode,
  isLoading,
  isUpdating = false,
  setDownloadColumnDefs,
  gridRef: externalGridRef,
  findSearchValue: externalFindSearchValue,
  onFindChanged: externalOnFindChanged,
  isSearchVisible = false,
  onCellValueChanged,
  onModelUpdated,
  tableEditable = true,
  onRenewalClick,
  onInfoClick,
  gridMenuResetSeq = 0,
}: DataListProps) {
  const internalGridRef = useRef<AgGridReact<StockRow>>(null)
  const gridRef = externalGridRef || internalGridRef

  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null)
  /**
   * 日付列ソート用のスナップショット。
   * key = product groupKey、value = ソート時にグリッドが実表示していた数値。
   * 入荷/出荷 が条件で複数フィールド合成される場合に、表示値そのものでソートするため
   * `api.getCellValue` を集計して保持する。
   */
  const [sortValueMap, setSortValueMap] = useState<Map<string, number> | null>(null)
  const [isRowGroupPanelVisible, setIsRowGroupPanelVisible] = useState(false)
  const [topGroupField, setTopGroupField] = useState<string>('supplierName')
  const [openedRowTypeGroups, setOpenedRowTypeGroups] = useState<Set<string>>(new Set())
  const [isGroupDisplayDirty, setIsGroupDisplayDirty] = useState(false)
  const [groupDisplayMode, setGroupDisplayMode] = useState<GroupDisplayMode>('default')
  const [displayFilter, setDisplayFilter] = useState<DisplayFilter | null>(null)
  const [itemSortTopKey, setItemSortTopKey] = useState<ItemSortTopKey | null>(null)
  const [itemSortExpectedRank, setItemSortExpectedRank] = useState(false)
  const [isSelectionSummaryFooterVisible, setIsSelectionSummaryFooterVisible] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SELECTION_SUMMARY_FOOTER_STORAGE_KEY) === 'true'
    } catch {
      return false
    }
  })
  /** 日付列:「アラート対象商品を上に表示」で並べ替え対象とした colId。null でオフ。 */
  const [alertSortTopColId, setAlertSortTopColId] = useState<string | null>(null)
  const displayFilterKey = displayFilterSig(displayFilter)

  const setSelectionSummaryFooterVisible = useCallback((visible: boolean) => {
    setIsSelectionSummaryFooterVisible(visible)
    try {
      localStorage.setItem(SELECTION_SUMMARY_FOOTER_STORAGE_KEY, visible ? 'true' : 'false')
    } catch {
      // ignore storage write errors
    }
  }, [])

  const itemSortTopKeyRef = useRef<ItemSortTopKey | null>(null)
  const [visibleLv3Groups, setVisibleLv3Groups] = useState<Set<string>>(
    new Set<string>(LV3_ITEM_GROUPS as unknown as string[])
  )
  const isEnforcingRowGroupRef = useRef(false)
  /** Lv3 rowType.group ノードキャッシュ（modelUpdated / gridReady で再構築） */
  const rowTypeGroupNodesRef = useRef<IRowNode<StockRow>[]>([])
  const prevMenuExpansionSigRef = useRef(DEFAULT_MENU_EXPANSION_SIG)
  const menuExpansionSigRef = useRef(DEFAULT_MENU_EXPANSION_SIG)
  const groupExpansionDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** グループ階層変更後、modelUpdated で項目メニュー設定を再適用する */
  const pendingMenuDisplayResyncRef = useRef(false)
  const itemSortExpectedRankRef = useRef(false)
  const sortConfigActiveRef = useRef(false)
  const displayFilterRef = useRef<DisplayFilter | null>(displayFilter)
  const groupDisplayModeRef = useRef(groupDisplayMode)
  const isGroupDisplayDirtyRef = useRef(isGroupDisplayDirty)
  const alertSortTopColIdRef = useRef<string | null>(null)
  const visibleLv3GroupsRef = useRef(visibleLv3Groups)
  const productFocusMatchCacheRef = useRef<Map<string, boolean>>(new Map())
  /** clearDisplayFilterOnly が同期で onFilterChanged 済みのとき useEffect の二重実行を防ぐ */
  const displayFilterExternalRefreshSkipRef = useRef(false)

  const rebuildRowTypeGroupNodeCache = useCallback((api: GridApi<StockRow>) => {
    if (!api || api.isDestroyed()) {
      rowTypeGroupNodesRef.current = []
      return
    }
    const nodes: IRowNode<StockRow>[] = []
    api.forEachNode((node) => {
      if (node.group && node.rowGroupColumn?.getColId?.() === 'rowType.group') {
        nodes.push(node as IRowNode<StockRow>)
      }
    })
    rowTypeGroupNodesRef.current = nodes
  }, [])

  /** キャッシュ優先で Lv3 を走査（空なら 1 回だけフルスキャンしてキャッシュ化） */
  const forEachRowTypeGroupNode = useCallback((
    api: GridApi<StockRow>,
    visit: (node: IRowNode<StockRow>) => void
  ) => {
    if (!api || api.isDestroyed()) {
      return
    }
    let cached = rowTypeGroupNodesRef.current
    if (cached.length === 0) {
      rebuildRowTypeGroupNodeCache(api)
      cached = rowTypeGroupNodesRef.current
    }
    for (let i = 0; i < cached.length; i += 1) {
      const node = cached[i]
      if (node?.group) {
        visit(node)
      }
    }
  }, [rebuildRowTypeGroupNodeCache])

  /** mainMenuItems は開いた瞬間に最新値を読む。日付列・項目列の colDef を安定的に保つ */
  const dayColumnMenuActionsRef = useRef<{
    sortConfig: SortConfig | null
    alertSortTopColId: string | null
    applySorting: (colId: string, sortBy: SortConfig['sortBy'], direction: SortConfig['direction']) => void
    resetSorting: () => void
    clearManualGroupMenuState: () => void
    selectAlertDisplayFilter: (colId: string, api: GridApi<StockRow>) => void
    toggleAlertSortTop: (colId: string) => void
  }>({
    sortConfig: null,
    alertSortTopColId: null,
    applySorting: () => {},
    resetSorting: () => {},
    clearManualGroupMenuState: () => {},
    selectAlertDisplayFilter: () => {},
    toggleAlertSortTop: () => {},
  })

  const itemColumnMenuUiRef = useRef<{
    itemSortTopKey: ItemSortTopKey | null
    itemSortExpectedRank: boolean
    visibleLv3Groups: Set<string>
    openedRowTypeGroups: Set<string>
    groupDisplayMode: GroupDisplayMode
    hasActiveDisplayFocus: boolean
    clearManualGroupMenuState: () => void
    selectItemDisplayFilter: (key: ItemDisplayFocusKey, api: GridApi<StockRow>) => void
    selectExpandAllGroups: (api: GridApi<StockRow>) => void
    toggleItemSortExpectedRank: () => void
    toggleItemSortTop: (key: ItemSortTopKey) => void
    restoreDefaultGroupExpansion: (api: GridApi<StockRow>) => void
  }>({
    itemSortTopKey: null,
    itemSortExpectedRank: false,
    visibleLv3Groups: new Set(),
    openedRowTypeGroups: new Set(),
    groupDisplayMode: 'default',
    hasActiveDisplayFocus: false,
    clearManualGroupMenuState: () => {},
    selectItemDisplayFilter: () => {},
    selectExpandAllGroups: () => {},
    toggleItemSortExpectedRank: () => {},
    toggleItemSortTop: () => {},
    restoreDefaultGroupExpansion: () => {},
  })

  /** User-expanded groups (level 3 e.g. 入荷); survives refreshCells / calcTotal / parent re-render. */
  const userExpandedGroupKeysRef = useRef<Set<string>>(new Set())
  const displayRowData = useMemo(
    () => buildSubtotalRowsByTopGroup(rowData, topGroupField),
    [rowData, topGroupField]
  )
  // Do not use ag-Grid auto-size for this column:
  // it measures rendered DOM cells repeatedly and can cause jank on large datasets.
  // We derive a deterministic width from alert-icon patterns per item group
  // to keep UI behavior stable while reducing reflow/repaint and render cost.
  const iconColumnWidth = useMemo(() => calculateItemColumnWidth(rowData), [rowData])

  useEffect(() => {
    userExpandedGroupKeysRef.current.clear()
    rowTypeGroupNodesRef.current = []
  }, [rowData])

  const openedRowTypeGroupsKey = useMemo(() => {
    const keys: string[] = []
    openedRowTypeGroups.forEach((k) => {
      keys.push(k)
    })
    return keys.sort().join(',')
  }, [openedRowTypeGroups])

  const menuExpansionSig = useMemo(
    () => buildMenuExpansionSig(
      groupDisplayMode,
      openedRowTypeGroupsKey,
      isGroupDisplayDirty
    ),
    [groupDisplayMode, openedRowTypeGroupsKey, isGroupDisplayDirty]
  )

  menuExpansionSigRef.current = menuExpansionSig
  itemSortExpectedRankRef.current = itemSortExpectedRank
  itemSortTopKeyRef.current = itemSortTopKey
  sortConfigActiveRef.current = sortConfig != null
  displayFilterRef.current = displayFilter
  groupDisplayModeRef.current = groupDisplayMode
  isGroupDisplayDirtyRef.current = isGroupDisplayDirty
  alertSortTopColIdRef.current = alertSortTopColId
  visibleLv3GroupsRef.current = visibleLv3Groups

  const isGroupOpenByDefault = useCallback((params: IsGroupOpenByDefaultParams<StockRow>) => {
    const node = params.rowNode
    if (!node?.group) {
      return false
    }
    return shouldStockGroupBeOpenByDefault(node, userExpandedGroupKeysRef.current)
  }, [])

  const onRowGroupOpened = useCallback((event: RowGroupOpenedEvent<StockRow>) => {
    const { node, expanded } = event
    if (!node?.group) {
      return
    }
    const path = buildStockGroupNodePath(node)
    if (!path) {
      return
    }
    if (expanded) {
      userExpandedGroupKeysRef.current.add(path)
    } else {
      userExpandedGroupKeysRef.current.delete(path)
    }
  }, [])

  const {
    localeText,
    isNonInputValue,
    parsePositiveInteger,
    parsePositiveIntegerZero,
    longTextValueParser,
    longTextValueFormatter,
  } = useGridBase({
    gridRef,
    isLoading,
    isUpdating,
    storageKey: 'gridColumnState:StockCenter2',
  })

  const defaultColDef = useMemo(() => {
    return {
      resizable: true,
      suppressHeaderMenuButton: true,
      sortable: false,
      filter: false,
    }
  }, [])

  const [isGridRendering, setIsGridRendering] = useState(false)

  // Tree-filter path tokens encode an `id__name` pair so distinct ids with the
  // same display name remain separate filter leaves. autoGroupFilterValueFormatter
  // strips the `id__` prefix when rendering the label.
  const getAutoGroupFilterPath = useCallback((row?: StockRow | null): string[] | null => {
    if (!row || row.isTotal) {
      return null
    }

    const supplierName = row.supplierName ?? ''
    const itemDisplay = row.itemName ?? row.orgItemCode ?? row.itemCode ?? ''
    const centerName = row.centerName ?? ''

    const supplierToken = row.orgSupplierId != null && row.orgSupplierId !== ''
      ? `${row.orgSupplierId}__${supplierName}`
      : supplierName
    const itemToken = row.itemCode != null && row.itemCode !== ''
      ? `${row.itemCode}__${itemDisplay}`
      : itemDisplay
    const centerToken = row.centerId != null
      ? `${row.centerId}__${centerName}`
      : centerName

    const filterPath = [supplierToken, itemToken, centerToken, row.rowType?.group ?? '']
      .filter((value): value is string => value !== '')

    return filterPath.length > 0 ? filterPath : null
  }, [])

  const autoGroupFilterValueGetter = useCallback((params: { data?: StockRow | null }) => {
    return getAutoGroupFilterPath(params.data)
  }, [getAutoGroupFilterPath])

  const autoGroupFilterPathGetter = useCallback((value: unknown) => {
    if (!Array.isArray(value)) {
      return null
    }

    const path = value.filter((item): item is string => typeof item === 'string' && item !== '')
    return path.length > 0 ? path : null
  }, [])

  const autoGroupFilterKeyCreator = useCallback((params: { value: unknown }) => {
    if (!Array.isArray(params.value)) {
      return null
    }

    return params.value.join('||')
  }, [])

  const autoGroupFilterValueFormatter = useCallback((params: { value: unknown }) => {
    if (!Array.isArray(params.value)) {
      return ''
    }

    const last = params.value[params.value.length - 1] ?? ''
    const text = String(last)
    const idx = text.indexOf('__')
    return idx >= 0 ? text.slice(idx + 2) : text
  }, [])

  useEffect(() => {
    if (isLoading || isUpdating) {
      setIsGridRendering(true)
    }
  }, [isLoading, isUpdating])

  useEffect(() => {
    if (displayRowData && displayRowData.length > 0) {
      setIsGridRendering(true)
    }
  }, [displayRowData])

  useEffect(() => {
    const api = gridRef.current?.api
    if (!api || api.isDestroyed()) {
      return
    }

    const shouldShowLoading = !!(isLoading || isUpdating || isGridRendering)

    api.setGridOption('loading', shouldShowLoading)
  }, [isLoading, isUpdating, isGridRendering])

  const hasScrolledRef = useRef(false)

  const displayDaylist = useMemo(
    () => getDisplayDaylistFromCenterSupplier(data?.daylistCenterSupplier),
    [data?.daylistCenterSupplier]
  )

  const hasActiveDisplayFocus = displayFilter != null

  const alertSortTopActive = useMemo(
    () => isAlertColIdActive(alertSortTopColId, displayDaylist),
    [alertSortTopColId, displayDaylist]
  )

  const activeProductSortKind = useMemo((): ActiveProductSortKind => {
    if (itemSortExpectedRank) {
      return 'expectedRank'
    }
    if (itemSortTopKey) {
      return itemSortTopKey
    }
    if (alertSortTopActive) {
      return 'alertTop'
    }
    return 'none'
  }, [itemSortExpectedRank, itemSortTopKey, alertSortTopActive])

  const displayDaylistRef = useRef(displayDaylist)
  displayDaylistRef.current = displayDaylist

  // Reset scroll flag when data changes (new search)
  useEffect(() => {
    hasScrolledRef.current = false
  }, [displayDaylist])

  const containerStyle = useMemo(() => {
    const searchBoxHeight = isSearchVisible ? 60 : 0
    return { width: '100%', height: `calc(100vh - ${240 + searchBoxHeight}px)` }
  }, [isSearchVisible])

  const gridStyle = useMemo(() => {
    const searchBoxHeight = isSearchVisible ? 60 : 0
    return { width: '100%', height: `calc(100vh - ${240 + searchBoxHeight}px)` }
  }, [isSearchVisible])

  const getheaderName = useCallback((day: Daylist) => {
    return day.datevalue ? format(day.datevalue, 'M/d(E)', { locale: ja }) : ''
  }, [])

  const getRowInGroup = useCallback((rowNode: IRowNode<StockRow>, rowTypeCode: string): IRowNode<StockRow> | undefined => {
    const groupKey =
      rowNode.data?.groupKey ??
      rowNode.allLeafChildren?.find((n) => n?.data?.groupKey)?.data?.groupKey

    let groupNode: IRowNode<StockRow> | null = rowNode.parent
    if (groupNode?.parent?.allLeafChildren) {
      groupNode = groupNode.parent
    }
    let current: IRowNode<StockRow> | null = rowNode
    while (current) {
      if (current.group && current.rowGroupColumn?.getColId() === 'centerName') {
        groupNode = current
        break
      }
      current = current.parent
    }

    const allLeaves = groupNode?.allLeafChildren
    if (!allLeaves) return undefined
    return allLeaves.find((row) => {
      if (row?.data?.rowType?.code !== rowTypeCode || row.data?.isTotal) return false
      if (groupKey) return row.data?.groupKey === groupKey
      return true
    })
  }, [])

  const beforeCol = useCallback((col: string) => {
    const index = parseInt(col.replace('col', ''), 10)
    return `col${index - 1}`
  }, [])

  useEffect(() => {
    const api = gridRef.current?.api
    if (!api || api.isDestroyed() || hasScrolledRef.current || !displayRowData || displayRowData.length === 0) {
      return
    }

    const todayCol = displayDaylist.find((day) => day.rectype === 'today')?.colname
    if (!todayCol) {
      return
    }

    const targetCol = beforeCol(beforeCol(todayCol))
    api.ensureColumnVisible(targetCol, 'start')
    hasScrolledRef.current = true
  }, [displayRowData, displayDaylist, beforeCol])

  /**
   * 入荷 / 出荷 グループ（rowType.group の Lv3 ノード）で実表示されている値を
   * groupKey 単位でスナップショット化する。
   * `api.getCellValue` を経由するため、valueGetter が条件分岐で
   * 複数フィールドから合成していてもセル表示値そのものでソートできる。
   */
  const collectSortValuesFromGrid = useCallback((
    colId: string,
    sortBy: 'arrival' | 'ship' | 'salesorder'
  ): Map<string, number> => {
    const map = new Map<string, number>()
    const api = gridRef.current?.api
    if (!api || api.isDestroyed()) {
      return map
    }

    if (sortBy === 'salesorder') {
      // 受注実績は出荷グループ配下の独立した行 (rowType.code = 'salesorderQuantity')。
      // 表示そのものは StockCell.value なので、リーフノードから直接読み出す。
      api.forEachNode((node) => {
        if (node.group) return
        const { data: leafData } = node
        if (!leafData || leafData.isTotal === true) return
        if (leafData.rowType?.code !== 'salesorderQuantity') return

        const productKey = leafData.groupKey
        if (!productKey || (typeof productKey === 'string' && productKey.startsWith('subtotal__'))) {
          return
        }
        if (map.has(productKey)) return

        const cell = leafData[colId as keyof StockRow] as StockCell | null | undefined
        let numValue = 0
        if (cell && cell.value != null && !isNonInputValue(cell.value)) {
          const parsed = Number(cell.value)
          if (!Number.isNaN(parsed)) {
            numValue = parsed
          }
        }
        map.set(productKey, numValue)
      })
    } else {
      const targetGroupKey = sortBy === 'arrival' ? '入荷' : '出荷'
      forEachRowTypeGroupNode(api, (node) => {
        if (node.key !== targetGroupKey) return

        const firstLeaf = node.allLeafChildren?.[0]?.data
        if (!firstLeaf || firstLeaf.isTotal === true) return

        const productKey = firstLeaf.groupKey
        if (!productKey || (typeof productKey === 'string' && productKey.startsWith('subtotal__'))) {
          return
        }
        if (map.has(productKey)) return

        const cellValue = api.getCellValue({ rowNode: node, colKey: colId }) as unknown
        const inner =
          cellValue != null &&
          typeof cellValue === 'object' &&
          'value' in (cellValue as Record<string, unknown>)
            ? (cellValue as { value: unknown }).value
            : cellValue

        let numValue = 0
        if (inner != null && !isNonInputValue(inner)) {
          const parsed = Number(inner)
          if (!Number.isNaN(parsed)) {
            numValue = parsed
          }
        }
        map.set(productKey, numValue)
      })
    }

    return map
  }, [isNonInputValue, forEachRowTypeGroupNode])

  /** 項目列の排他ソート（出荷予測精度 / 上に表示×3 / アラート上）をすべて解除 */
  const clearProductSortOptions = useCallback(() => {
    setItemSortExpectedRank(false)
    setItemSortTopKey(null)
    setAlertSortTopColId(null)
  }, [])

  const clearDateColumnSort = useCallback(() => {
    setSortConfig(null)
    setSortValueMap(null)
  }, [])

  const applySorting = useCallback((
    colId: string,
    sortBy: 'arrival' | 'ship' | 'salesorder',
    direction: 'asc' | 'desc'
  ) => {
    const api = gridRef.current?.api
    if (!api || api.isDestroyed()) {
      return
    }

    clearProductSortOptions()

    // ag-grid 側のソート（autoGroup / 仕入先 / 商品 / センター / rowType.group）を一旦解除し、
    // rowData の並びがそのままグループノードの並びとして反映されるようにする。
    api.applyColumnState({
      defaultState: { sort: null },
    })

    const valueMap = collectSortValuesFromGrid(colId, sortBy)
    setSortValueMap(valueMap)
    setSortConfig({ colId, sortBy, direction })
  }, [collectSortValuesFromGrid, clearProductSortOptions])

  /**
   * ソートをリセットして元の順序に戻す
   */
  const resetSorting = useCallback(() => {
    const api = gridRef.current?.api
    if (!api || api.isDestroyed()) {
      return
    }

    // rowData の投入順（StockCenter 側の並び）に戻す。
    api.applyColumnState({
      defaultState: { sort: null },
    })

    setSortValueMap(null)
    setSortConfig(null)
  }, [])

  /** グループ展開を初期表示（Lv0〜2 まで開く、Lv3 は閉じる）に戻す。フィルターは触らない。 */
  const restoreDefaultGroupExpansion = useCallback((api: GridApi<StockRow>) => {
    if (!api || api.isDestroyed()) {
      return
    }
    userExpandedGroupKeysRef.current.clear()
    api.collapseAll()
    api.forEachNode((node) => {
      if (node.group && node.level != null && node.level < DEFAULT_GROUP_EXPANDED_LEVEL) {
        api.setRowNodeExpanded(node, true)
      }
    })
  }, [])

  /** フィルター / グループを開く / すべて開く を初期化（focus 適用前に呼ぶ） */
  const clearManualGroupMenuState = useCallback(() => {
    setVisibleLv3Groups(ALL_LV3_GROUPS_VISIBLE_SET)
    setOpenedRowTypeGroups(new Set())
    setIsGroupDisplayDirty(false)
    setGroupDisplayMode('default')
  }, [])

  const toggleItemSortExpectedRank = useCallback(() => {
    setItemSortExpectedRank((prev) => {
      const turningOn = !prev
      if (turningOn) {
        clearDateColumnSort()
        setItemSortTopKey(null)
        setAlertSortTopColId(null)
      }
      return turningOn
    })
  }, [clearDateColumnSort])

  const toggleItemSortTop = useCallback((key: ItemSortTopKey) => {
    setItemSortTopKey((prev) => {
      if (prev === key) {
        return null
      }
      clearDateColumnSort()
      setItemSortExpectedRank(false)
      setAlertSortTopColId(null)
      return key
    })
  }, [clearDateColumnSort])

  const toggleAlertSortTop = useCallback((colId: string) => {
    setAlertSortTopColId((prev) => {
      if (prev === colId) {
        return null
      }
      clearDateColumnSort()
      setItemSortExpectedRank(false)
      setItemSortTopKey(null)
      return colId
    })
  }, [clearDateColumnSort])

  const applyDisplayFilterToGrid = useCallback((api?: GridApi<StockRow> | null) => {
    productFocusMatchCacheRef.current.clear()
    const apiGrid = api && !api.isDestroyed() ? api : gridRef.current?.api ?? null
    if (apiGrid && !apiGrid.isDestroyed()) {
      apiGrid.onFilterChanged()
    }
  }, [gridRef])

  /** 同一フィルター再選択時のみ即時反映。フィルター変更は useEffect 1 回に任せる（二重 onFilterChanged 防止）。 */
  const triggerDisplayFilterRefresh = useCallback((api?: GridApi<StockRow> | null) => {
    applyDisplayFilterToGrid(api)
  }, [applyDisplayFilterToGrid])

  /**
   * 表示フィルターのみ解除（並び替え・グループ展開 state は維持）。
   * ref を同期で null にしてから onFilterChanged 1 回 → expand 前に filter が確実に解除される。
   */
  const clearDisplayFilterOnly = useCallback((api?: GridApi<StockRow> | null) => {
    if (displayFilterRef.current == null) {
      return false
    }
    displayFilterRef.current = null
    displayFilterExternalRefreshSkipRef.current = true
    setDisplayFilter(null)
    applyDisplayFilterToGrid(api)
    return true
  }, [applyDisplayFilterToGrid])

  const expandAllLv3GroupNodes = useCallback((api: GridApi<StockRow>) => {
    forEachRowTypeGroupNode(api, (node) => {
      if (!isTotalOnlyGroupNode(node) && !node.expanded) {
        api.setRowNodeExpanded(node, true)
      }
    })
  }, [forEachRowTypeGroupNode])

  const selectItemDisplayFilter = useCallback((
    key: ItemDisplayFocusKey,
    api?: GridApi<StockRow> | null
  ) => {
    const next: DisplayFilter = { kind: 'item', key }
    if (isSameDisplayFilter(displayFilterRef.current, next)) {
      triggerDisplayFilterRefresh(api)
      return
    }
    clearManualGroupMenuState()
    setDisplayFilter(next)
  }, [clearManualGroupMenuState, triggerDisplayFilterRefresh])

  const selectAlertDisplayFilter = useCallback((
    colId: string,
    api?: GridApi<StockRow> | null
  ) => {
    const next: DisplayFilter = { kind: 'alert', colId }
    if (isSameDisplayFilter(displayFilterRef.current, next)) {
      triggerDisplayFilterRefresh(api)
      return
    }
    clearManualGroupMenuState()
    setDisplayFilter(next)
  }, [clearManualGroupMenuState, triggerDisplayFilterRefresh])

  /** すべてのグループを開く：先に表示フィルターを解除してから Lv3 をすべて展開（並びは維持）。 */
  const selectExpandAllGroups = useCallback((api?: GridApi<StockRow> | null) => {
    const apiGrid = api && !api.isDestroyed() ? api : gridRef.current?.api ?? null
    if (!apiGrid || apiGrid.isDestroyed()) {
      return
    }

    clearDisplayFilterOnly(apiGrid)

    const alreadyExpandedAll =
      groupDisplayModeRef.current === 'expanded' &&
      isGroupDisplayDirtyRef.current

    if (alreadyExpandedAll) {
      expandAllLv3GroupNodes(apiGrid)
      return
    }

    setIsGroupDisplayDirty(true)
    setGroupDisplayMode('expanded')
    setOpenedRowTypeGroups(ALL_LV3_GROUPS_SET)
    expandAllLv3GroupNodes(apiGrid)
  }, [clearDisplayFilterOnly, expandAllLv3GroupNodes, gridRef])

  /** 項目列メニュー「表示をデフォルトに戻す」と同等の UI 状態リセット */
  const resetItemColumnDisplayToDefault = useCallback((api?: GridApi<StockRow> | null) => {
    prevMenuExpansionSigRef.current = DEFAULT_MENU_EXPANSION_SIG
    if (groupExpansionDebounceRef.current != null) {
      clearTimeout(groupExpansionDebounceRef.current)
      groupExpansionDebounceRef.current = null
    }
    setDisplayFilter(null)
    clearProductSortOptions()
    clearDateColumnSort()
    clearManualGroupMenuState()
    setIsRowGroupPanelVisible(false)

    const apiGrid =
      api && !api.isDestroyed() ? api : gridRef.current?.api ?? null
    if (!apiGrid || apiGrid.isDestroyed()) {
      userExpandedGroupKeysRef.current.clear()
      return
    }

    apiGrid.setFilterModel(null)
    apiGrid.applyColumnState({
      defaultState: { sort: null },
    })
    restoreDefaultGroupExpansion(apiGrid)
  }, [clearProductSortOptions, clearDateColumnSort, clearManualGroupMenuState, restoreDefaultGroupExpansion, gridRef])

  const gridMenuResetPrevSeq = useRef<number | null>(null)
  useEffect(() => {
    if (gridMenuResetPrevSeq.current === null) {
      gridMenuResetPrevSeq.current = gridMenuResetSeq
      return
    }
    if (gridMenuResetPrevSeq.current === gridMenuResetSeq) {
      return
    }
    gridMenuResetPrevSeq.current = gridMenuResetSeq

    resetItemColumnDisplayToDefault(gridRef.current?.api ?? null)
  }, [gridMenuResetSeq, resetItemColumnDisplayToDefault, gridRef])

  /** 日付列 alert_hight と同じ条件（getRowInGroup で商品単位の行を解決） */
  const computeProductHasAlertForCol = useCallback((
    anchorLeaf: IRowNode<StockRow> | undefined,
    colId: string
  ): boolean => {
    if (!anchorLeaf) {
      return false
    }

    const rowTypes = ['stock_quantity', 'avgShipQuantityStockBase', 'stockDays', 'shipQuantity'] as const
    const rows = rowTypes
      .map((code) => getRowInGroup(anchorLeaf, code)?.data)
      .filter((r): r is StockRow => !!r)

    return productRowsHaveAlertForCol(rows, colId, displayDaylist)
  }, [displayDaylist, getRowInGroup])

  const computeProductHasAlertForColRef = useRef(computeProductHasAlertForCol)
  computeProductHasAlertForColRef.current = computeProductHasAlertForCol

  /** グループを開く / すべて開く のみ反映（focus 表示は external filter で制御） */
  const applyGroupExpansionFromMenuState = useCallback((api: GridApi<StockRow>) => {
    if (!api || api.isDestroyed()) {
      return
    }

    const sig = menuExpansionSig
    const prevSig = prevMenuExpansionSigRef.current
    prevMenuExpansionSigRef.current = sig

    const hasMenuExpansion = sig !== DEFAULT_MENU_EXPANSION_SIG

    if (!hasMenuExpansion) {
      if (prevSig !== DEFAULT_MENU_EXPANSION_SIG) {
        userExpandedGroupKeysRef.current.clear()
        forEachRowTypeGroupNode(api, (node) => {
          if (!isTotalOnlyGroupNode(node) && node.expanded) {
            api.setRowNodeExpanded(node, false)
          }
        })
      }
      return
    }

    forEachRowTypeGroupNode(api, (node) => {
      if (isTotalOnlyGroupNode(node)) {
        return
      }

      const lv3Key = String(node.key ?? '')
      let shouldExpand = false

      if (groupDisplayMode === 'expanded') {
        shouldExpand = true
      } else if (groupDisplayMode === 'collapsed') {
        shouldExpand = false
      } else {
        shouldExpand = openedRowTypeGroups.has(lv3Key)
      }

      if (node.expanded !== shouldExpand) {
        api.setRowNodeExpanded(node, shouldExpand)
      }
    })
  }, [
    menuExpansionSig,
    groupDisplayMode,
    openedRowTypeGroups,
    forEachRowTypeGroupNode,
  ])

  const textSort = useCallback((a: string, b: string) => {
    const aIsTotal = a.includes('合計')
    const bIsTotal = b.includes('合計')

    if (aIsTotal && !bIsTotal) {
      return -1
    }
    if (!aIsTotal && bIsTotal) {
      return 1
    }
    if (a < b) {
      return -1
    }
    if (a > b) {
      return 1
    }
    return 0
  }, [])

  const isCellEditable = useCallback(
    (colEditable: boolean, rowTypeCode: string | undefined, obj: StockCell | null | undefined): boolean => {
      if (!rowTypeCode || !tableEditable || unitType === 'pallet') {
        return false
      }

      if (rowTypeCode === 'remarks') {
        return true
      }

      const rowEditable = ['arrivalQuantityPlan', 'shipQuantityPlan'].includes(rowTypeCode)
      const cellEditable = obj?.enableUpdate || false
      const result = rowEditable && colEditable && cellEditable

      return result
    },
    [tableEditable, unitType]
  )

  const valueFormatter = useCallback(
    (params: ValueFormatterParams<StockRow, StockCell>) => {
      const obj = params.value
      let rowTypeCode = params.data?.rowType?.code
      if (!rowTypeCode && params.node?.group && params.node?.level === 3) {
        const groupKey = params.node.key
        switch (groupKey) {
          case '入荷': rowTypeCode = 'arrivalQuantity'; break
          case '出荷': rowTypeCode = 'shipQuantity'; break
          case '在庫数': rowTypeCode = 'stock_quantity'; break
          case '在庫日数': rowTypeCode = 'stockDays'; break
          case '備考': rowTypeCode = 'remarks'; break
          default: break
        }
      }

      if (!obj || isNonInputValue(obj)) return ''

      // パレット単位の場合のフォーマット（全数量行）
      if (unitType === 'pallet' && [
        'arrivalQuantity', 'orderQuantityRec', 'orderQuantityRecommend',
        'arrivalQuantityPlan', 'orderQuantity',
        'shipQuantity', 'shipQuantityExpected', 'salesorderQuantity', 'shipQuantityPlan', 'shipQuantityScheduled',
        'stock_quantity', 'avgShipQuantityStockBase', 'stockProduct', 'calcSafety', 'stockQuantityShipStop', 'shipQuantityStockBase'
      ].includes(rowTypeCode || '')) {
        if (isNonInputValue(obj.value)) return '-'
        const rawValue = Number(obj.value)
        if (Number.isNaN(rawValue)) return '-'
        const palletDivisor = Number(
          params.data?.maxPalletQuantity ??
          params.node?.allLeafChildren?.[0]?.data?.maxPalletQuantity ??
          0
        )
        const palletValue = palletDivisor > 0 ? rawValue / palletDivisor : rawValue
        return palletValue.toFixed(1)
      }

      // 備考行のフォーマット
      if (rowTypeCode === 'remarks') {
        return longTextValueFormatter({ ...params, value: obj.value })
      }

      // 安全在庫数は整数表示（在庫数と同様に小数を出さない）
      if (rowTypeCode === 'calcSafety') {
        if (isNonInputValue(obj.value)) return '-'
        if (Number.isNaN(obj.value)) return '-'
        const val = Number(obj.value)
        if (val <= 0) return '0'
        // if (val > 999) return '999 +'
        return val.toFixed(0)
      }

      // 在庫日数 / 翌日～7営業日出荷数平均 / 在庫商品行のフォーマット
      if (rowTypeCode === 'stockDays' || rowTypeCode === 'avgShipQuantityStockBase' || rowTypeCode === 'stockProduct') {
        if (isNonInputValue(obj.value)) return '-'
        if (Number.isNaN(obj.value)) return '-'
        const val = Number(obj.value)
        if (val <= 0) return '0'
        if (val > 999 && rowTypeCode === 'stockDays') return '999 +'
        return val.toFixed(1)
      }

      // デフォルトのフォーマット
      if (isNonInputValue(obj.value)) return ''
      return obj.value.toString()
    },
    [unitType, isNonInputValue, longTextValueFormatter]
  )

  const getResolvedRowTypeCode = useCallback(
    (rowTypeCode: string | undefined, node?: IRowNode<StockRow>) => {
      if (rowTypeCode) {
        return rowTypeCode
      }

      return node?.allLeafChildren?.[0]?.data?.rowType?.code
    },
    []
  )

  const cellClass = useCallback(
    ({
      colEditable,
      rectype,
      obj,
      rowTypeCode,
      field,
      node,
    }: {
      colEditable: boolean
      rectype: string
      obj: StockCell | null | undefined
      rowTypeCode: string
      field: string | undefined
      node: IRowNode<StockRow>
    }) => {
      // 合計行（subtotal leaf row も含む）はアラート系クラスを一切付けない。
      // today 列のみ today ハイライトを残し、それ以外は素のセルにする。
      if (isTotalOnlyGroupNode(node) || node.data?.isTotal === true) {
        if (rectype === 'today') {
          return ['number-cell', 'todays-order-cell']
        }
        return ['number-cell']
      }

      const classes = ['number-cell']
      const resolvedRowTypeCode = getResolvedRowTypeCode(rowTypeCode, node)

      if (obj?.isOrderConfirmed && rectype === 'expected') {
        classes.push('order-confirmed')
      }
      if (['arrival', 'arrivalQuantity'].includes(resolvedRowTypeCode || '')) {
        if (obj?.orderDate && systemDate && isEqualDate(obj?.orderDate, systemDate) && obj?.isTodayOrderDay && obj?.isArrivalDay) {
          classes.push('todays-order-cell')
        }
      }

      if (['stock', 'stock_quantity'].includes(resolvedRowTypeCode || '')) {
        const stockRowAvg = getRowInGroup(node, 'avgShipQuantityStockBase')
        const stockRowStockQuantityRec = getRowInGroup(node, 'stockQuantityRec')
        const stockRowShipStop = getRowInGroup(node, 'stockQuantityShipStop')
        const stockCellAvg = stockRowAvg?.data?.[field as keyof StockRow] as StockCell
        const stockCellStockQuantityRec = stockRowStockQuantityRec?.data?.[field as keyof StockRow] as StockCell
        const minStockQuantity = obj?.minStockQuantity
        const stockCellShipStop = stockRowShipStop?.data?.[field as keyof StockRow] as StockCell | undefined
        if (['today', 'expected'].includes(rectype)
          && obj?.value != null
          && obj.value <= 0
          && stockCellAvg?.value != null
          && stockCellAvg.value > 0) {
          classes.push('alert_hight')
        }

        if (['today', 'expected'].includes(rectype) && 
            ((stockCellAvg?.value != null
            && stockCellAvg.value > 0
            && minStockQuantity != null
            && obj?.value != null
            && obj.value < minStockQuantity) || (stockCellShipStop?.value != null))
          ){
          classes.push('alert_middle')
        }

        if (stockCellStockQuantityRec?.value == null
          && ['rec'].includes(rectype))
          {
            classes.push('alert_low')
          }
        }

      if (resolvedRowTypeCode === 'stockDays') {
        const stockRowAvg = getRowInGroup(node, 'avgShipQuantityStockBase')
        const stockCellAvg = stockRowAvg?.data?.[field as keyof StockRow] as StockCell
        const shipdaysNextArrival = obj?.shipdaysNextArrival
        const stockDaysSetting = obj?.stockDaysSetting
        if (['today', 'expected'].includes(rectype)
          && obj?.value != null
          && shipdaysNextArrival != null
          && obj.value <= shipdaysNextArrival) {
          classes.push('alert_hight')
        }
        if (['today', 'expected'].includes(rectype)
          && stockCellAvg?.value != null
          && stockCellAvg.value > 0
          && stockDaysSetting != null
          && obj?.value != null
          && obj.value <= stockDaysSetting
      ) {
          classes.push('alert_middle')
        }
      }

      if (['ship', 'shipQuantity'].includes(resolvedRowTypeCode || '') && field) {
        const stockRow = getRowInGroup(node, 'stock_quantity')
        if (stockRow) {
          if (obj?.value != null && obj.value > 0 && ['today', 'expected'].includes(rectype)) {
            const prevStockCell = stockRow.data?.[beforeCol(field) as keyof StockRow] as StockCell
            if (prevStockCell?.value != null && obj.value >= prevStockCell.value && obj.value > 0) {
              classes.push('alert_hight')
            }
          }
          if (obj?.value != null && obj.value > 0 && ['expected'].includes(rectype)) {
            const salesOrderRow = getRowInGroup(node, 'salesorderQuantity')
            const orderQuantityRecRow = getRowInGroup(node, 'orderQuantityRec')
            const salesOrderCell = salesOrderRow?.data?.[field as keyof StockRow] as StockCell
            const orderQuantityRecCell = orderQuantityRecRow?.data?.[field as keyof StockRow] as StockCell

            if (salesOrderCell?.value != null && orderQuantityRecCell?.value == null) {
              classes.push('alert_low')
            }
          }
        }
      }

      if (['salesOrder', 'salesorderQuantity'].includes(resolvedRowTypeCode || '') && field) {
        const orderQuantityRecRow = getRowInGroup(node, 'orderQuantityRec')
        const orderQuantityRecCell = orderQuantityRecRow?.data?.[field as keyof StockRow] as StockCell
        if (['expected'].includes(rectype)
          && obj?.value != null
          && orderQuantityRecCell?.value == null)
          {
          classes.push('alert_low')
        }
      }

      // Show editable mark only on leaf rows (actual data rows), not on group rows.
      if (!node.group && isCellEditable(colEditable, resolvedRowTypeCode, obj)) {
        classes.push('editable-cell')
      }

      if (obj && obj.isCurrentUpdate) {
        classes.push('current-updated-cell')
      }

      if (obj && obj.isUpdate
        //  && !obj.isOrderConfirmed
        ) {
        classes.push('updated-cell')
      }

      if (rectype === 'today' && !(node.level === 0 || node.level === 1 || node.level === 2)) {
        classes.push('todays-order-cell')
      }

      if (['orderQuantityRec'].includes(resolvedRowTypeCode || '')) {
        const hasOrderQuantityRec = obj?.value !== null && obj?.value !== undefined
        if (rectype === 'expected' && hasOrderQuantityRec) {
          classes.push('future-arrival-exists')
        }
      }

      if (['orderQuantityRecommend'].includes(resolvedRowTypeCode || '')) {
        const hasOrderQuantityRecommend = obj?.value !== null && obj?.value !== undefined && obj?.value > 0
        if (['today', 'expected'].includes(rectype) && hasOrderQuantityRecommend) {
          classes.push('order-recommend')
        }
        if (['today', 'expected'].includes(rectype) && hasOrderQuantityRecommend && obj?.minStockQuantity != null) {
          classes.push('alert_low')
        }
      }

      if (['orderQuantity'].includes(resolvedRowTypeCode || '')) {
        const hasOrderQuantity = obj?.value !== null && obj?.value !== undefined
        if (rectype === 'expected' && hasOrderQuantity && obj?.isOrderConfirmed) {
          classes.push('order-confirmed')
        }
        if (obj?.isUpdatedArrivalTmp) {
          classes.push('updated-tmp-cell')
        }
      }

      if (['arrival', 'arrivalQuantity'].includes(resolvedRowTypeCode || '')) {
        const hasOrderQuantityRec = (obj?.orderQuantityRec !== null && obj?.orderQuantityRec !== undefined)
        const hasOrderQuantityRecommend = (obj?.orderQuantityRecommend !== null && obj?.orderQuantityRecommend !== undefined && obj?.orderQuantityRecommend > 0)
        const arrivalFixFlg = obj?.fixFlg
        // const orderToday = obj?.orderDate && systemDate && isEqualDate(obj?.orderDate, systemDate)
        if (['today', 'expected'].includes(rectype)
          && !obj?.isUpdatedArrivalTmp
          && hasOrderQuantityRecommend
          && !hasOrderQuantityRec
          && !arrivalFixFlg
          // && !obj?.isOrderConfirmed
          // && !orderToday
        ) {
          classes.push('order-recommend')
        }
        if (rectype === 'expected'
          // && !obj?.isUpdatedArrivalTmp
          && hasOrderQuantityRec
          // && !obj?.isOrderConfirmed
        ) {
          classes.push('future-arrival-exists')
        }
      }

      return classes
    },
    [beforeCol, getResolvedRowTypeCode, getRowInGroup, isCellEditable, systemDate]
  )

  const valueParser = useCallback((params: ValueParserParams) => {
    const { newValue, oldValue } = params
    let updatedValue: string | number = newValue
    const { isCurrentUpdate, value: oldVal } = oldValue || {}
    const isUpdateData = (isCurrentUpdate === true) || (updatedValue !== oldVal)

    if (isNonInputValue(updatedValue)) {
      updatedValue = ''
    }

    if (['shipQuantityPlan', 'arrivalQuantityPlan'].includes(params.data?.rowType?.code || '')) {
      const parsedValue = parsePositiveIntegerZero(newValue)
      updatedValue = parsedValue !== null ? parsedValue : ''
    }

    if (params.data?.rowType?.code === 'remarks' && typeof newValue === 'string') {
      const parsedValue = longTextValueParser({ ...params, newValue })
      updatedValue = parsedValue !== null ? parsedValue : ''
    }

    let stockPlanUpdateDatetime = oldValue?.stockPlanUpdateDatetime

    if (!stockPlanUpdateDatetime && isUpdateData) {
      const stockDataRecord = data.list.find((stock: Stock) =>
        stock.recDate &&
        oldValue?.recDate &&
        stock.centerId === params.data?.centerId &&
        stock.itemCode === params.data?.itemCode &&
        isEqualDate(stock.recDate, oldValue.recDate)
      )
      stockPlanUpdateDatetime = stockDataRecord?.stockPlanUpdateDatetime
    }

    return {
      ...oldValue,
      value: updatedValue,
      isCurrentUpdate: isUpdateData,
      stockPlanUpdateDatetime,
    }
  }, [isNonInputValue, parsePositiveInteger, parsePositiveIntegerZero, longTextValueParser, data.list])

  const groupPanelTextComparator = useCallback((
    valueA?: string | null,
    valueB?: string | null,
    nodeA?: IRowNode<StockRow>,
    nodeB?: IRowNode<StockRow>
  ) => {
    const aLeaf = (nodeA?.allLeafChildren?.[0]?.data ?? nodeA?.data) as StockRow | undefined
    const bLeaf = (nodeB?.allLeafChildren?.[0]?.data ?? nodeB?.data) as StockRow | undefined

    const aTot = isStockRowSubtotalOrTotal(aLeaf)
    const bTot = isStockRowSubtotalOrTotal(bLeaf)
    if (aTot !== bTot) {
      // 合計行は同階層の先頭（上）に固定
      return aTot ? -1 : 1
    }

    if (!aTot && !bTot && activeProductSortKind !== 'none') {
      if (activeProductSortKind === 'alertTop' && alertSortTopColId) {
        const aNode = nodeA as IRowNode<StockRow> | null | undefined
        const bNode = nodeB as IRowNode<StockRow> | null | undefined
        const aAlert = aNode ? computeProductHasAlertForColRef.current(aNode, alertSortTopColId) : false
        const bAlert = bNode ? computeProductHasAlertForColRef.current(bNode, alertSortTopColId) : false
        if (aAlert !== bAlert) {
          return aAlert ? -1 : 1
        }
      } else if (aLeaf && bLeaf) {
        const cmp = compareProductGroupSortEntries(
          { rows: [], index: 0, sample: aLeaf },
          { rows: [], index: 0, sample: bLeaf },
          activeProductSortKind,
          alertSortTopColId,
          null
        )
        if (cmp !== 0) {
          return cmp
        }
      }
    }

    return textSort(valueA ?? '', valueB ?? '')
  }, [activeProductSortKind, alertSortTopColId, textSort])

  const groupPanelHeaderValueGetter = useCallback(
    (params: HeaderValueGetterParams<StockRow>) => {
      const field = params.column?.getColDef().field ?? ''

      if (!field) {
        return ''
      }

      if (!isRowGroupPanelVisible || params.location !== 'columnDrop') {
        return field
      }

      return GROUP_PANEL_LABELS[field] ?? field
    },
    [isRowGroupPanelVisible]
  )

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams<StockRow>): Array<DefaultMenuItem | MenuItemDef<StockRow>> => {
      const toggleItem: MenuItemDef<StockRow> = {
        name: isRowGroupPanelVisible ? '閉じる' : 'グループ変更',
        action: () => {
          params.api.hidePopupMenu()
          setIsRowGroupPanelVisible(!isRowGroupPanelVisible)
        },
      }

      const selectionSummaryItem: MenuItemDef<StockRow> = {
        name: isSelectionSummaryFooterVisible ? 'フッター閉じる' : 'フッター表示',
        action: () => {
          params.api.hidePopupMenu()
          setSelectionSummaryFooterVisible(!isSelectionSummaryFooterVisible)
        },
      }

      const defaultItems = (params.defaultItems ?? []) as Array<DefaultMenuItem | MenuItemDef<StockRow>>

      if (defaultItems.length === 0) {
        return [toggleItem, 'separator' as DefaultMenuItem, selectionSummaryItem]
      }

      return [toggleItem, selectionSummaryItem, 'separator' as DefaultMenuItem, ...defaultItems]
    },
    [isRowGroupPanelVisible, isSelectionSummaryFooterVisible, setSelectionSummaryFooterVisible]
  )

  useEffect(() => {
    const api = gridRef.current?.api
    if (!api || api.isDestroyed()) {
      return
    }

    api.setGridOption('rowGroupPanelShow', isRowGroupPanelVisible ? 'always' : 'never')
    api.refreshHeader()
  }, [gridRef, isRowGroupPanelVisible])

  dayColumnMenuActionsRef.current = {
    sortConfig,
    alertSortTopColId,
    applySorting,
    resetSorting,
    clearManualGroupMenuState,
    selectAlertDisplayFilter,
    toggleAlertSortTop,
  }

  const genDayCols = useCallback((rectype: string, colEditable: boolean, visibleColCount: number) => {
    if (!displayDaylist.length) {
      return []
    }

    const startIndex = startDateTypeCode ? Math.abs(Number(startDateTypeCode)) : 0
    const endIndex = endDateTypeCode ? Number(endDateTypeCode) : 14
    const totalVisibleDays = startIndex + endIndex

    const filteredDays = displayDaylist.filter((day: Daylist) => day.rectype === rectype)

    return filteredDays
      .sort((a: Daylist, b: Daylist) => (a.dateno || 0) - (b.dateno || 0))
      .map((day: Daylist, idx: number) => {
        const dayDateno = day.dateno || 0
        const shouldHide = dayDateno === 0 || dayDateno > totalVisibleDays

        const colDef = {
          headerName: getheaderName(day),
          field: day.colname ? day.colname : undefined,
          width: 110,
          hide: shouldHide,
          type: 'rightAligned',
          headerClass: clsx(`value-header-${rectype}`, {
            sunday: day.datevalue?.getDay() === 0,
          }),
          suppressHeaderMenuButton: false,
          suppressMenu: false,
          /** 日付列はカスタム mainMenuItems のみ。filter タブは非表示（空の第2タブ防止） */
          menuTabs: ['generalMenuTab'] as ColumnMenuTab[],
          editable: (params: EditableCallbackParams<StockRow>) => {
            const obj = params.data?.[params.colDef.field as keyof StockRow] as StockCell
            return isCellEditable(colEditable, params.data?.rowType?.code, obj)
          },
          valueGetter: (params: any) => {
            if (params.node?.group) {
              if (params.node.level !== 3) return null
              const groupKey = params.node.key
              const { field } = params.colDef
              if (!field) return null

              let targetCode = ''
              switch (groupKey) {
                case '入荷': targetCode = 'arrivalQuantity'; break
                case '出荷': targetCode = 'shipQuantity'; break
                case '在庫数': targetCode = 'stock_quantity'; break
                case '在庫日数': targetCode = 'stockDays'; break
                case '備考': targetCode = 'remarks'; break
                default: return null
              }

              const children = params.node.allLeafChildren || []
              for (let i = 0; i < children.length; i += 1) {
                if (children[i].data?.rowType?.code === targetCode) {
                  return children[i].data?.[field] ?? null
                }
              }
              return null
            }
            const { field } = params.colDef
            if (field && params.data) {
              return params.data[field]
            }
            return null
          },
          valueFormatter,
          cellClass: (params: CellClassParams<StockRow>) =>
            cellClass({
              colEditable,
              rectype,
              obj: params.value,
              rowTypeCode: params.data?.rowType?.code || '',
              field: params.colDef.field,
              node: params.node,
            }),
          valueParser,
          cellEditorSelector: (params: ICellEditorParams<StockRow>) => {
            switch (params.data?.rowType?.code) {
              case 'remarks': {
                let raw = params.value?.value ?? ''
                if (raw.startsWith('"') && raw.endsWith('"') && /[\r\n]/.test(raw)) {
                  raw = raw.slice(1, -1)
                }
                return {
                  component: 'agLargeTextCellEditor',
                  popup: true,
                  params: {
                    maxLength: 1000,
                    value: raw,
                  },
                }
              }
              case 'arrival':
              case 'ship':
                return {
                  component: StockCellEditor,
                }
              default:
                return {
                  component: StockCellEditor,
                }
            }
          },
          mainMenuItems: (params: GetMainMenuItemsParams<StockRow>) => {
            const colId = params.column?.getColId()
            const menu = dayColumnMenuActionsRef.current

            if (!colId || !/^col\d+$/.test(colId)) {
              return params.defaultItems
            }

            const customItems: (string | MenuItemDef<StockRow>)[] = [
              {
                name: 'アラート対象商品を表示',
                action: () => {
                  menu.selectAlertDisplayFilter(colId, params.api)
                  params.api.hidePopupMenu()
                },
              },
              {
                name: 'アラート対象商品を上に表示',
                disabled: !['today', 'expected'].includes(rectype),
                action: () => {
                  menu.toggleAlertSortTop(colId)
                  params.api.hidePopupMenu()
                },
                icon: menu.alertSortTopColId === colId ? '<span>✓</span>' : undefined,
              },
              'separator',
              {
                name: 'この日付で並び替え',
                subMenu: [
                  {
                    name: '入荷数量 (昇順)',
                    action: () => menu.applySorting(colId, 'arrival', 'asc'),
                    icon: menu.sortConfig?.colId === colId && menu.sortConfig?.sortBy === 'arrival' && menu.sortConfig?.direction === 'asc'
                      ? '<span>✓</span>'
                      : undefined,
                  },
                  {
                    name: '入荷数量 (降順)',
                    action: () => menu.applySorting(colId, 'arrival', 'desc'),
                    icon: menu.sortConfig?.colId === colId && menu.sortConfig?.sortBy === 'arrival' && menu.sortConfig?.direction === 'desc'
                      ? '<span>✓</span>'
                      : undefined,
                  },
                  'separator',
                  {
                    name: '出荷数量 (昇順)',
                    action: () => menu.applySorting(colId, 'ship', 'asc'),
                    icon: menu.sortConfig?.colId === colId && menu.sortConfig?.sortBy === 'ship' && menu.sortConfig?.direction === 'asc'
                      ? '<span>✓</span>'
                      : undefined,
                  },
                  {
                    name: '出荷数量 (降順)',
                    action: () => menu.applySorting(colId, 'ship', 'desc'),
                    icon: menu.sortConfig?.colId === colId && menu.sortConfig?.sortBy === 'ship' && menu.sortConfig?.direction === 'desc'
                      ? '<span>✓</span>'
                      : undefined,
                  },
                  'separator',
                  {
                    name: '受注実績 (昇順)',
                    action: () => menu.applySorting(colId, 'salesorder', 'asc'),
                    icon: menu.sortConfig?.colId === colId && menu.sortConfig?.sortBy === 'salesorder' && menu.sortConfig?.direction === 'asc'
                      ? '<span>✓</span>'
                      : undefined,
                  },
                  {
                    name: '受注実績 (降順)',
                    action: () => menu.applySorting(colId, 'salesorder', 'desc'),
                    icon: menu.sortConfig?.colId === colId && menu.sortConfig?.sortBy === 'salesorder' && menu.sortConfig?.direction === 'desc'
                      ? '<span>✓</span>'
                      : undefined,
                  },
                  'separator',
                  {
                    name: 'デフォルトに戻す',
                    action: () => menu.resetSorting(),
                    disabled: !menu.sortConfig,
                  },
                ],
              },
            ]

            return customItems
          },
        }

        return colDef
      })
  }, [
    displayDaylist,
    getheaderName,
    isCellEditable,
    valueFormatter,
    cellClass,
    valueParser,
    startDateTypeCode,
    endDateTypeCode,
  ])

  /** 項目列表示：グループ Lv2 は orgItemCode、リーフは rowType.name。 */
  const itemColumnDisplayValueGetter = useCallback((params: ValueGetterParams<StockRow>) => {
    if (params.node?.group && params.node?.level === 2) {
      return params.node.allLeafChildren?.[0]?.data?.orgItemCode || ''
    }
    if (params.node?.group && params.node?.level === 3) {
      return ''
    }
    return params.data?.rowType?.name
  }, [])

  /** 項目列フィルターー：orgItemCode のみ（ノードは data または先頭リーフから取得）。 */
  const itemColumnOrgItemCodeFilterGetter = useCallback((params: ValueGetterParams<StockRow>) => {
    if (params.data?.orgItemCode != null && String(params.data.orgItemCode) !== '') {
      return String(params.data.orgItemCode)
    }
    const firstLeaf = params.node?.allLeafChildren?.[0]?.data
    return firstLeaf?.orgItemCode != null ? String(firstLeaf.orgItemCode) : ''
  }, [])

  const menuAdjustedRowData = useMemo(() => {
    if (activeProductSortKind === 'none') {
      return displayRowData
    }

    const productGroups = new Map<string, ProductGroupSortEntry & { topKey: string }>()
    const topGroupOrder: string[] = []
    const topGroupEntries = new Map<string, string[]>()
    const untouchedRows: StockRow[] = []
    let groupIndex = 0

    displayRowData.forEach((row) => {
      if (row.isTotal || !row.groupKey || row.groupKey.startsWith('subtotal__')) {
        untouchedRows.push(row)
        return
      }

      const key = row.groupKey
      const topKey = getTopGroupMeta(row, topGroupField).key

      if (!productGroups.has(key)) {
        productGroups.set(key, {
          rows: [],
          index: groupIndex,
          sample: row,
          topKey,
        })
        groupIndex += 1
        if (!topGroupEntries.has(topKey)) {
          topGroupEntries.set(topKey, [])
          topGroupOrder.push(topKey)
        }
        topGroupEntries.get(topKey)!.push(key)
      }
      productGroups.get(key)!.rows.push(row)
    })

    let alertFlagByGroupKey: Map<string, boolean> | null = null
    if (activeProductSortKind === 'alertTop' && alertSortTopColId) {
      alertFlagByGroupKey = new Map()
      productGroups.forEach((entry, groupKey) => {
        alertFlagByGroupKey!.set(
          groupKey,
          productRowsHaveAlertForCol(entry.rows, alertSortTopColId, displayDaylist)
        )
      })
    }

    const sortedDataRows: StockRow[] = []
    topGroupOrder.forEach((topKey) => {
      const groupKeys = topGroupEntries.get(topKey) ?? []
      const entries = groupKeys
        .map((groupKey) => productGroups.get(groupKey))
        .filter((entry): entry is ProductGroupSortEntry & { topKey: string } => !!entry)

      entries.sort((a, b) => compareProductGroupSortEntries(
        a,
        b,
        activeProductSortKind,
        alertSortTopColId,
        alertFlagByGroupKey
      ))

      entries.forEach((entry) => {
        sortedDataRows.push(...entry.rows)
      })
    })

    return [...untouchedRows, ...sortedDataRows]
  }, [
    displayRowData,
    activeProductSortKind,
    alertSortTopColId,
    displayDaylist,
    topGroupField,
  ])

  /**
   * 日付列ソート（applySorting）の結果を rowData prop に永続反映する。
   * `setGridOption('rowData', ...)` で書き換えると React の prop 更新で簡単に
   * 元に戻ってしまうため、rowData 自体を memoize したソート済み配列に差し替える。
   * sortValueMap は applySorting のスナップショットなので、編集や再描画があっても
   * 並び順は維持される（値の取り直しが必要なときはユーザーが再度ソートを実行する）。
   */
  const sortedDisplayData = useMemo(() => {
    if (!sortConfig || !sortValueMap || sortValueMap.size === 0) {
      return menuAdjustedRowData
    }

    const totalRows: StockRow[] = []
    const groupedByKey = new Map<string, StockRow[]>()
    const orderInData: string[] = []

    menuAdjustedRowData.forEach((row) => {
      if (
        row.isTotal === true ||
        (typeof row.groupKey === 'string' && row.groupKey.startsWith('subtotal__'))
      ) {
        totalRows.push(row)
        return
      }
      const key = (row.groupKey ?? row.orgItemCode ?? '') as string
      if (!groupedByKey.has(key)) {
        groupedByKey.set(key, [])
        orderInData.push(key)
      }
      groupedByKey.get(key)!.push(row)
    })

    const dir = sortConfig.direction
    const sortedKeys = orderInData.slice().sort((a, b) => {
      const va = sortValueMap.get(a) ?? 0
      const vb = sortValueMap.get(b) ?? 0
      if (va === vb) return 0
      return dir === 'asc' ? va - vb : vb - va
    })

    const result: StockRow[] = [...totalRows]
    sortedKeys.forEach((key) => {
      const rows = groupedByKey.get(key)
      if (rows) {
        result.push(...rows)
      }
    })

    return result
  }, [menuAdjustedRowData, sortConfig, sortValueMap])

  const statusBar = useMemo(() => ({
    statusPanels: [
      {
        statusPanel: 'agTotalAndFilteredRowCountComponent',
        align: 'left',
      },
      {
        statusPanel: 'renderAwareAggregationStatusPanel',
        align: 'right',
      },
    ],
  }), [])

  const components = useMemo(() => ({
    renderAwareAggregationStatusPanel: RenderAwareAggregationStatusPanel,
  }), [])

  useEffect(() => {
    if (menuExpansionSig === DEFAULT_MENU_EXPANSION_SIG) {
      return
    }
    queueMicrotask(() => {
      const api = gridRef.current?.api
      if (api && !api.isDestroyed()) {
        applyGroupExpansionFromMenuState(api)
      }
    })
  }, [menuExpansionSig, sortedDisplayData, applyGroupExpansionFromMenuState, gridRef])

  useEffect(() => {
    const api = gridRef.current?.api
    if (!api || api.isDestroyed()) {
      return
    }
    api.refreshClientSideRowModel('sort')
  }, [gridRef, itemSortExpectedRank, itemSortTopKey, alertSortTopColId])

  itemColumnMenuUiRef.current = {
    itemSortTopKey,
    itemSortExpectedRank,
    visibleLv3Groups,
    openedRowTypeGroups,
    groupDisplayMode,
    hasActiveDisplayFocus,
    clearManualGroupMenuState,
    selectItemDisplayFilter,
    selectExpandAllGroups,
    toggleItemSortExpectedRank,
    toggleItemSortTop,
    restoreDefaultGroupExpansion,
  }

  const columnDefs = useMemo<(ColDef<StockRow> | ColGroupDef<StockRow>)[]>(() => [
    {
      field: "supplierName",
      enableRowGroup: true,
      rowGroup: true,
      hide: true,
      sortable: true,
      // Distinct (orgSupplierId) → distinct group node, even when supplierName collides.
      // Subtotal rows are routed via resolveSubtotalGroupKey so the "合計" block nests
      // under its real top-group instead of leaking into id-keyed real groups.
      keyCreator: (p: KeyCreatorParams<StockRow>) => {
        const subtotalKey = resolveSubtotalGroupKey(p.data ?? null, 'supplier')
        if (subtotalKey != null) return subtotalKey
        return p.data?.orgSupplierId != null && p.data.orgSupplierId !== ''
          ? String(p.data.orgSupplierId)
          : 'unknown_supplier'
      },
      // Group nodes are now keyed by id; sort labels by display name for UX parity.
      comparator: (_a: unknown, _b: unknown, nodeA?: IRowNode<StockRow> | null, nodeB?: IRowNode<StockRow> | null) => {
        const nameA = nodeA?.allLeafChildren?.[0]?.data?.supplierName ?? ''
        const nameB = nodeB?.allLeafChildren?.[0]?.data?.supplierName ?? ''
        return groupPanelTextComparator(nameA, nameB)
      },
      headerValueGetter: groupPanelHeaderValueGetter,
    },
    {
      field: "itemName",
      enableRowGroup: true,
      rowGroup: true,
      hide: true,
      sortable: true,
      keyCreator: (p: KeyCreatorParams<StockRow>) => {
        const subtotalKey = resolveSubtotalGroupKey(p.data ?? null, 'item')
        if (subtotalKey != null) return subtotalKey
        return p.data?.itemCode != null && p.data.itemCode !== ''
          ? String(p.data.itemCode)
          : 'unknown_item'
      },
      comparator: (_a: unknown, _b: unknown, nodeA?: IRowNode<StockRow> | null, nodeB?: IRowNode<StockRow> | null) => {
        const nameA = nodeA?.allLeafChildren?.[0]?.data?.itemName ?? ''
        const nameB = nodeB?.allLeafChildren?.[0]?.data?.itemName ?? ''
        return groupPanelTextComparator(nameA, nameB)
      },
      headerValueGetter: groupPanelHeaderValueGetter,
    },
    {
      field: "centerName",
      enableRowGroup: true,
      rowGroup: true,
      hide: true,
      sortable: true,
      keyCreator: (p: KeyCreatorParams<StockRow>) => {
        const subtotalKey = resolveSubtotalGroupKey(p.data ?? null, 'center')
        if (subtotalKey != null) return subtotalKey
        return p.data?.centerId != null ? String(p.data.centerId) : 'unknown_center'
      },
      comparator: (_a: unknown, _b: unknown, nodeA?: IRowNode<StockRow> | null, nodeB?: IRowNode<StockRow> | null) => {
        const nameA = nodeA?.allLeafChildren?.[0]?.data?.centerName ?? ''
        const nameB = nodeB?.allLeafChildren?.[0]?.data?.centerName ?? ''
        return groupPanelTextComparator(nameA, nameB)
      },
      headerValueGetter: groupPanelHeaderValueGetter,
    },
    {
      field: "rowType.group",
      rowGroup: true,
      hide: true,
      suppressMovable: true,
      headerValueGetter: groupPanelHeaderValueGetter,
      comparator: (valueA: string, valueB: string) => {
        const orderA = ROW_TYPE_GROUP_SORT_ORDER[valueA] ?? 99
        const orderB = ROW_TYPE_GROUP_SORT_ORDER[valueB] ?? 99
        return orderA - orderB
      }
    },
    {
      headerName: '項目',
      field: "rowType.name",
      hide: false,
      width: iconColumnWidth,
      pinned: 'left',
      suppressHeaderMenuButton: false,
      filter: 'agMultiColumnFilter',
      filterParams: {
        filters: [
          {
            filter: 'agTextColumnFilter',
          },
          {
            filter: 'agSetColumnFilter',
            filterParams: {
              comparator: textSort,
              applyMiniFilterWhileTyping: true,
              excelMode: 'mac',
              buttons: ['reset'],
            },
          },
        ],
      },
      menuTabs: ['filterMenuTab', 'generalMenuTab'],
      mainMenuItems: (params: GetMainMenuItemsParams<StockRow>) => {
        const u = itemColumnMenuUiRef.current

        const lv3FilterSubMenu: MenuItemDef<StockRow>[] = LV3_ITEM_GROUPS.map((name) => ({
          name,
          suppressCloseOnSelect: true,
          menuItem: Lv3CheckboxMenuItem,
          menuItemParams: {
            checked: u.visibleLv3Groups.has(name),
            getChecked: () => itemColumnMenuUiRef.current.visibleLv3Groups.has(name),
            onCheckedChange: (checked: boolean) => {
              setVisibleLv3Groups((prev) => {
                const next = new Set(prev)
                if (checked) {
                  next.add(name)
                } else {
                  next.delete(name)
                }
                return next
              })
            },
          },
        }))

        const exitExpandAllForManualGroupPick = () => {
          setIsGroupDisplayDirty(true)
          // expanded / collapsed のときは手動「グループを開く」が無効になるため default に戻す
          setGroupDisplayMode('default')
        }

        const isLv3GroupOpenChecked = (name: string) => {
          const menu = itemColumnMenuUiRef.current
          return menu.groupDisplayMode === 'expanded' || menu.openedRowTypeGroups.has(name)
        }

        const groupOpenSubMenu: MenuItemDef<StockRow>[] = LV3_ITEM_GROUPS.map((name) => ({
          name,
          suppressCloseOnSelect: true,
          menuItem: Lv3CheckboxMenuItem,
          menuItemParams: {
            checked: isLv3GroupOpenChecked(name),
            getChecked: () => isLv3GroupOpenChecked(name),
            onCheckedChange: (checked: boolean) => {
              exitExpandAllForManualGroupPick()
              setOpenedRowTypeGroups((prev) => {
                const next = new Set(prev)
                if (checked) {
                  next.add(name)
                } else {
                  next.delete(name)
                }
                return next
              })
            },
          },
        }))

        return [
          {
            name: '出荷予測アラートを表示',
            action: () => {
              u.selectItemDisplayFilter('expectedAlert', params.api)
              params.api.hidePopupMenu()
            },
          },
          {
            name: '出荷予測精度で並び替え',
            action: () => u.toggleItemSortExpectedRank(),
            icon: u.itemSortExpectedRank ? '<span>✓</span>' : undefined,
          },
          'separator',
          {
            name: 'リニューアル商品を表示',
            action: () => {
              u.selectItemDisplayFilter('renewal', params.api)
              params.api.hidePopupMenu()
            },
          },
          {
            name: 'リニューアル商品を上に表示',
            action: () => u.toggleItemSortTop('renewalTop'),
            icon: u.itemSortTopKey === 'renewalTop' ? '<span>✓</span>' : undefined,
          },
          'separator',
          {
            name: '新商品を表示',
            action: () => {
              u.selectItemDisplayFilter('newItem', params.api)
              params.api.hidePopupMenu()
            },
          },
          {
            name: '新商品を上に表示',
            action: () => u.toggleItemSortTop('newItemTop'),
            icon: u.itemSortTopKey === 'newItemTop' ? '<span>✓</span>' : undefined,
          },
          'separator',
          {
            name: '終売品を表示',
            action: () => {
              u.selectItemDisplayFilter('endSale', params.api)
              params.api.hidePopupMenu()
            },
          },
          {
            name: '終売品を上に表示',
            action: () => u.toggleItemSortTop('endSaleTop'),
            icon: u.itemSortTopKey === 'endSaleTop' ? '<span>✓</span>' : undefined,
          },
          'separator',
          {
            name: 'フィルター',
            disabled: u.hasActiveDisplayFocus,
            subMenu: lv3FilterSubMenu,
          },
          {
            name: 'グループを開く',
            subMenuRole: 'listbox',
            /** expanded モード中は手動チェックと二重適用になるため default に戻す（openedRowTypeGroups は維持） */
            action: () => {
              exitExpandAllForManualGroupPick()
            },
            subMenu: groupOpenSubMenu,
          },
          {
            name: 'すべてのグループを開く',
            action: () => {
              u.selectExpandAllGroups(params.api)
              params.api.hidePopupMenu()
            },
          },
          'separator',
          {
            name: '表示をデフォルトに戻す',
            action: () => {
              resetItemColumnDisplayToDefault(params.api)
              params.api.hidePopupMenu()
            },
          },
        ]
      },
      filterValueGetter: itemColumnOrgItemCodeFilterGetter,
      valueGetter: itemColumnDisplayValueGetter,
      cellClassRules: {
        alert_hight: (params: CellClassParams<StockRow>) => {
          if (params.node?.group) {
            // Rank is rendered on the "出荷" group row (level 3).
            if (params.node.level === 3 && params.node.key === '出荷' && !isTotalOnlyGroupNode(params.node)) {
              return !!params.node.allLeafChildren?.some(
                (child) => child?.data?.isExpectedRankAlert === true
              )
            }
            return false
          }
          return null
        },
      } as any,
      getFindText: ({ node, data: findRowData }) => getItemColumnFindText(node, findRowData),
      cellRenderer: ActionCellRenderer,
      cellRendererParams: (params: any) => {
        const leafChildren = params.node?.allLeafChildren ?? []
        const dataValue = leafChildren.map((n: any) => n.data).filter(Boolean)
        return {
          suppressCount: true,
          dataValue,
          onRenewalClick,
        }
      },
      onCellClicked: (params: CellClickedEvent<StockRow>) => {
        const mouseEvent = params.event as MouseEvent;
        const target = mouseEvent?.target as HTMLElement | null;

        if (!target) return;

        if (target.closest('.action-icon-info')) {
          mouseEvent.stopPropagation();
          if (onInfoClick) {
            const { aggData } = params.node;
            const fallbackLeaf = params.node?.allLeafChildren?.[0]?.data;
            const centerIdRaw = aggData?.centerId ?? fallbackLeaf?.centerId ?? null
            const itemCode = String(aggData?.itemCode ?? fallbackLeaf?.itemCode ?? '');
            const centerId = typeof centerIdRaw === 'number' ? centerIdRaw : Number(centerIdRaw)

            if (!Number.isNaN(centerId) && itemCode) {
              onInfoClick(centerId, itemCode);
            }
          }
          return;
        }

        if (target.closest('.action-icon-chart')) {
          mouseEvent.stopPropagation();
          const { aggData } = params.node;
          if (aggData) {
            const { companyId, centerId, itemCode } = aggData;
            const url = `/${protectedRoutes.Dashboard.path}?company_id=${companyId}&center_id=${centerId}&item_code=${itemCode}&date_range=year`;
            window.open(url, '_blank');
          }
          return;
        }

        const renewalElement = target.closest('.action-icon-renewal') as HTMLElement | null;
        if (renewalElement) {
          mouseEvent.stopPropagation();
          const isRenewalAvailable = renewalElement.getAttribute('data-has-renewal') === 'true';

          if (isRenewalAvailable && onRenewalClick) {
            const leafChildren = params.node?.allLeafChildren ?? [];
            const renewalLeaf = leafChildren.find((n) => n.data?.renewalFlg === true);
            if (renewalLeaf?.data) {
              const { centerId, itemCode } = renewalLeaf.data;
              onRenewalClick(true, String(centerId), itemCode ?? '');
            }
          }
        }
      },
    },
    {
      field: 'companyId',
      hide: true,
      aggFunc: (params: IAggFuncParams<StockRow>) => params.rowNode?.allLeafChildren?.[0]?.data?.companyId,
    },
    {
      field: 'centerId',
      hide: true,
      aggFunc: (params: IAggFuncParams<StockRow>) => params.rowNode?.allLeafChildren?.[0]?.data?.centerId,
    },
    {
      field: 'itemCode',
      hide: true,
      aggFunc: (params: IAggFuncParams<StockRow>) => params.rowNode?.allLeafChildren?.[0]?.data?.itemCode,
    },
    {
      groupId: HEADER_GROUP_REC,
      headerName: '実績',
      headerClass: 'value-header-rec',
      children: genDayCols('rec', false, Number(endDateTypeCode)),
    },
    {
      groupId: HEADER_GROUP_TODAY,
      headerName: '当日',
      headerClass: 'value-header-today',
      children: genDayCols('today', true, Number(endDateTypeCode)),
    },
    {
      groupId: HEADER_GROUP_EXPECTED,
      headerName: '予定',
      headerClass: 'value-header-expected',
      children: genDayCols('expected', true, Number(endDateTypeCode)),
    },
  ], [
    genDayCols,
    endDateTypeCode,
    textSort,
    onRenewalClick,
    onInfoClick,
    groupPanelHeaderValueGetter,
    groupPanelTextComparator,
    itemColumnDisplayValueGetter,
    itemColumnOrgItemCodeFilterGetter,
    iconColumnWidth,
    resetItemColumnDisplayToDefault,
  ])

  useEffect(() => {
    if (displayFilterExternalRefreshSkipRef.current) {
      displayFilterExternalRefreshSkipRef.current = false
      return
    }
    productFocusMatchCacheRef.current.clear()
    const api = gridRef.current?.api
    if (!api || api.isDestroyed()) {
      return
    }
    api.onFilterChanged()
  }, [gridRef, visibleLv3Groups, displayFilterKey])

  /**
   * グループ階層変更後に項目メニューの表示設定を grid へ再反映する。
   * - 展開: グループを開く / すべて開く
   * - 並び: 出荷予測精度 / 上に表示 / 日付列ソート（rowData は既に更新済み、CSRM sort を refresh）
   */
  const syncMenuDisplayAfterGridStructureChange = useCallback((api: GridApi<StockRow>) => {
    if (!api || api.isDestroyed()) {
      return
    }

    const hasExpansionMenu =
      menuExpansionSigRef.current !== DEFAULT_MENU_EXPANSION_SIG
    const hasItemColumnSort =
      itemSortExpectedRankRef.current ||
      itemSortTopKeyRef.current != null ||
      sortConfigActiveRef.current ||
      isAlertColIdActive(alertSortTopColIdRef.current, displayDaylistRef.current)

    if (!hasExpansionMenu && !hasItemColumnSort) {
      rebuildRowTypeGroupNodeCache(api)
      return
    }

    userExpandedGroupKeysRef.current.clear()
    rebuildRowTypeGroupNodeCache(api)

    if (hasExpansionMenu) {
      applyGroupExpansionFromMenuState(api)
    }

    if (hasItemColumnSort) {
      api.refreshClientSideRowModel('sort')
    }
  }, [rebuildRowTypeGroupNodeCache, applyGroupExpansionFromMenuState])

  useEffect(() => {
    const api = gridRef.current?.api
    if (!api || api.isDestroyed()) {
      return undefined
    }

    if (groupExpansionDebounceRef.current != null) {
      clearTimeout(groupExpansionDebounceRef.current)
    }

    groupExpansionDebounceRef.current = setTimeout(() => {
      groupExpansionDebounceRef.current = null
      const currentApi = gridRef.current?.api
      if (currentApi && !currentApi.isDestroyed()) {
        applyGroupExpansionFromMenuState(currentApi)
      }
    }, GROUP_EXPANSION_DEBOUNCE_MS)

    return () => {
      if (groupExpansionDebounceRef.current != null) {
        clearTimeout(groupExpansionDebounceRef.current)
        groupExpansionDebounceRef.current = null
      }
    }
  }, [menuExpansionSig, applyGroupExpansionFromMenuState, gridRef, rowData])

  useEffect(() => () => {
    if (groupExpansionDebounceRef.current != null) {
      clearTimeout(groupExpansionDebounceRef.current)
    }
  }, [])

  const autoGroupColumnDef = useMemo<ColDef<StockRow>>(() => {
    const formatCodeName = (code: string | number | null | undefined, name: string | null | undefined): string => {
      const codeText = code != null && String(code).trim() !== '' ? String(code) : ''
      const nameText = name != null && String(name).trim() !== '' ? String(name) : ''

      if (codeText && nameText) {
        return `[${codeText}]${nameText}`
      }
      if (nameText) {
        return nameText
      }
      if (codeText) {
        return `[${codeText}]`
      }
      return ''
    }

    const groupLabelRenderer = (params: ICellRendererParams<StockRow>) => {
      const renderWithFind = (rawText: unknown) => {
        const text = rawText != null ? String(rawText) : ''
        if (!text) {
          return text
        }
        return renderItemColumnLabelWithFind(
          params.api,
          params.column ?? undefined,
          params.node as IRowNode<StockRow>,
          text
        )
      }

      if (!params.node?.group) {
        return renderWithFind(params.value)
      }
      if (isTotalOnlyGroupNode(params.node)) {
        return renderWithFind(params.value)
      }
      if (typeof params.value === 'string' && params.value.includes('合計')) {
        return renderWithFind(params.value)
      }

      const firstLeaf = params.node.allLeafChildren?.[0]?.data
      if (!firstLeaf) {
        return params.value
      }

      const groupColId = params.node.rowGroupColumn?.getColId?.() ?? ''

      switch (groupColId) {
        case 'supplierName':
          return renderWithFind(formatCodeName(firstLeaf.orgSupplierId, firstLeaf.supplierName))
        case 'itemName':
          return renderWithFind(formatCodeName(firstLeaf.orgItemCode ?? firstLeaf.itemCode, firstLeaf.itemName))
        case 'centerName':
          return renderWithFind(formatCodeName(firstLeaf.centerId, firstLeaf.centerName))
        default:
          return renderWithFind(params.value)
      }
    }

    return {
      // rowData の投入順を維持するため autoGroup 列のデフォルトソートは行わない。
      sort: null,
      minWidth: 200,
      width: 280,
      pinned: 'left',
      suppressHeaderMenuButton: false,
      floatingFilter: false,
      suppressHeaderFilterButton: true,
      menuTabs: ['filterMenuTab'] as ColumnMenuTab[],
      filter: 'agSetColumnFilter',
      filterValueGetter: autoGroupFilterValueGetter,
      filterParams: {
        treeList: true,
        keyCreator: autoGroupFilterKeyCreator,
        treeListPathGetter: autoGroupFilterPathGetter,
        valueFormatter: autoGroupFilterValueFormatter,
        applyMiniFilterWhileTyping: true,
      },
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: groupLabelRenderer,
      },
    }
  }, [
    autoGroupFilterKeyCreator,
    autoGroupFilterPathGetter,
    autoGroupFilterValueFormatter,
    autoGroupFilterValueGetter,
  ])

  /**
   * rowData 投入後や項目側ソート（日付列 / 出荷予測精度 / 上に表示）が有効なとき、
   * ag-grid 内部で列ソート状態が復活しないよう常に解除する（StockCenter の rowData 順を維持）。
   */
  useEffect(() => {
    const api = gridRef.current?.api
    if (!api || api.isDestroyed()) {
      return
    }
    api.applyColumnState({
      defaultState: { sort: null },
    })
  }, [
    gridRef,
    sortConfig,
    activeProductSortKind,
    sortedDisplayData,
  ])

  const isExternalFilterPresent = useCallback(() => {
    // Always enable external filter so baseline duplicate-row hiding is always active.
    return true
  }, [])

  const doesExternalFilterPass = useCallback((node: IRowNode) => {
    const row = node.data as StockRow | undefined
    const rowTypeCode = row?.rowType?.code
    if (rowTypeCode && DUPLICATE_LEVEL4_ROWTYPE_CODES.has(rowTypeCode)) {
      return false
    }

    if (row?.isTotal === true) {
      return true
    }
    const groupKey = row?.groupKey
    if (typeof groupKey === 'string' && groupKey.startsWith('subtotal__')) {
      return true
    }

    const groupName = row?.rowType?.group
    const activeFilter = displayFilterRef.current
    const hasDisplayFocus = activeFilter != null

    if (hasDisplayFocus && groupName && row) {
      const productKey = row.groupKey ?? ''
      if (productKey && !productKey.startsWith('subtotal__')) {
        let matches = productFocusMatchCacheRef.current.get(productKey)
        if (matches === undefined) {
          if (activeFilter.kind === 'item') {
            matches = sampleRowMatchesDisplayFocus(row, activeFilter.key)
          } else if (isAlertColIdActive(activeFilter.colId, displayDaylistRef.current)) {
            matches = computeProductHasAlertForColRef.current(
              node as IRowNode<StockRow>,
              activeFilter.colId
            )
          } else {
            matches = false
          }
          productFocusMatchCacheRef.current.set(productKey, matches)
        }
        if (!matches && groupName !== '入荷') {
          return false
        }
      }
    }

    if (groupName && !visibleLv3GroupsRef.current.has(groupName)) {
      return false
    }

    return true
  }, [])

  const normalizeNonRowTypeGroupIds = useCallback((groupIds: string[]) => {
    const filtered = groupIds.filter((colId) => DEFAULT_NON_ROWTYPE_GROUP_ORDER.includes(colId as any))
    const missingDefaults = DEFAULT_NON_ROWTYPE_GROUP_ORDER.filter((colId) => !filtered.includes(colId))
    return [...filtered, ...missingDefaults]
  }, [])

  const applyRowGroupOrder = useCallback((api: any, nonRowTypeGroupIds: string[]) => {
    const filtered = normalizeNonRowTypeGroupIds(nonRowTypeGroupIds)
    const desiredGroupColIds = [...filtered, 'rowType.group']
    api.applyColumnState({
      state: desiredGroupColIds.map((colId, idx) => ({
        colId,
        rowGroup: true,
        rowGroupIndex: idx,
        hide: true,
      })),
      defaultState: {
        rowGroup: false,
        rowGroupIndex: null,
      },
    })
    const nextTop = filtered[0] ?? 'supplierName'
    if (nextTop !== topGroupField) {
      pendingMenuDisplayResyncRef.current = true
    }
    setTopGroupField(nextTop)
  }, [normalizeNonRowTypeGroupIds, topGroupField])

  const enforceRowGroupOrder = useCallback((api: any) => {
    if (!api || isEnforcingRowGroupRef.current) {
      return
    }

    const currentGroupColIds: string[] = api.getRowGroupColumns().map((col: any) => col.getColId())
    const nonRowTypeGroupIds = currentGroupColIds.filter((colId) => colId !== 'rowType.group')
    const normalizedNonRowTypeGroupIds = normalizeNonRowTypeGroupIds(nonRowTypeGroupIds)
    const desiredGroupColIds = [...normalizedNonRowTypeGroupIds, 'rowType.group']

    const isSameOrder =
      currentGroupColIds.length === desiredGroupColIds.length &&
      currentGroupColIds.every((colId, idx) => colId === desiredGroupColIds[idx])

    if (!isSameOrder) {
      isEnforcingRowGroupRef.current = true
      applyRowGroupOrder(api, normalizedNonRowTypeGroupIds)
      isEnforcingRowGroupRef.current = false
      pendingMenuDisplayResyncRef.current = true
    }

    const nextTop = normalizedNonRowTypeGroupIds[0] ?? 'supplierName'
    if (nextTop !== topGroupField) {
      pendingMenuDisplayResyncRef.current = true
    }
    setTopGroupField(nextTop)
  }, [applyRowGroupOrder, normalizeNonRowTypeGroupIds, topGroupField])

  const loadSavedRowGroupOrder = useCallback((): string[] | null => {
    try {
      const raw = localStorage.getItem(ROW_GROUP_ORDER_STORAGE_KEY)
      if (!raw) {
        return null
      }
      const parsed = JSON.parse(raw)
      if (!Array.isArray(parsed)) {
        return null
      }
      const result = parsed.filter((colId) => typeof colId === 'string' && DEFAULT_NON_ROWTYPE_GROUP_ORDER.includes(colId as any))
      return normalizeNonRowTypeGroupIds(result)
    } catch {
      return null
    }
  }, [normalizeNonRowTypeGroupIds])

  const saveRowGroupOrder = useCallback(() => {
    const api = gridRef.current?.api
    if (!api || api.isDestroyed()) {
      return
    }
    const currentGroupColIds: string[] = api.getRowGroupColumns().map((col: any) => col.getColId())
    const nonRowTypeGroupIds = currentGroupColIds.filter((colId) => colId !== 'rowType.group')
    const filtered = normalizeNonRowTypeGroupIds(nonRowTypeGroupIds)
    localStorage.setItem(ROW_GROUP_ORDER_STORAGE_KEY, JSON.stringify(filtered))
    setIsRowGroupPanelVisible(false)
  }, [gridRef, normalizeNonRowTypeGroupIds])

  const resetRowGroupOrder = useCallback(() => {
    const api = gridRef.current?.api
    if (!api || api.isDestroyed()) {
      return
    }
    localStorage.removeItem(ROW_GROUP_ORDER_STORAGE_KEY)
    isEnforcingRowGroupRef.current = true
    applyRowGroupOrder(api, [...DEFAULT_NON_ROWTYPE_GROUP_ORDER])
    isEnforcingRowGroupRef.current = false
    pendingMenuDisplayResyncRef.current = true
    enforceRowGroupOrder(api)
  }, [applyRowGroupOrder, enforceRowGroupOrder, gridRef])

  const closeRowGroupPanel = useCallback(() => {
    setIsRowGroupPanelVisible(false)
  }, [])

  const handleGridReady = useCallback((event: GridReadyEvent) => {
    event.api.setGridOption('loading', !!(isLoading || isUpdating || isGridRendering))

    if (setDownloadColumnDefs) {
      setDownloadColumnDefs(event.api.getColumnDefs())
    }
    const saved = loadSavedRowGroupOrder()
    if (saved && saved.length > 0) {
      isEnforcingRowGroupRef.current = true
      applyRowGroupOrder(event.api, saved)
      isEnforcingRowGroupRef.current = false
      pendingMenuDisplayResyncRef.current = true
    }
    enforceRowGroupOrder(event.api)
    rebuildRowTypeGroupNodeCache(event.api)
  }, [
    setDownloadColumnDefs,
    loadSavedRowGroupOrder,
    applyRowGroupOrder,
    enforceRowGroupOrder,
    rebuildRowTypeGroupNodeCache,
    isLoading,
    isUpdating,
    isGridRendering,
  ])

  const handleColumnRowGroupChanged = useCallback((event: any) => {
    enforceRowGroupOrder(event.api)
    pendingMenuDisplayResyncRef.current = true
    rebuildRowTypeGroupNodeCache(event.api)
  }, [enforceRowGroupOrder, rebuildRowTypeGroupNodeCache])

  const handleModelUpdated = useCallback(
    (event: ModelUpdatedEvent<StockRow>) => {
      setIsGridRendering(false)
      const api = gridRef.current?.api
      if (api && !api.isDestroyed()) {
        rebuildRowTypeGroupNodeCache(api)
        if (pendingMenuDisplayResyncRef.current) {
          pendingMenuDisplayResyncRef.current = false
          queueMicrotask(() => {
            if (!api.isDestroyed()) {
              syncMenuDisplayAfterGridStructureChange(api)
            }
          })
        }
      }
      onModelUpdated?.(event)
    },
    [onModelUpdated, rebuildRowTypeGroupNodeCache, syncMenuDisplayAfterGridStructureChange, gridRef]
  )

  const handleSortChanged = useCallback(() => {
    const api = gridRef.current?.api
    if (!api || api.isDestroyed()) {
      return
    }

    const hasGroupPanelSort = api.getColumnState().some((columnState) =>
      (GROUP_PANEL_SORT_FIELDS as readonly string[]).includes(columnState.colId) &&
      !!columnState.sort
    )

    if (hasGroupPanelSort) {
      setSortValueMap(null)
      setSortConfig(null)
    }
  }, [gridRef])

  return (
    <div style={containerStyle}>
      <div className="stock-grid-wrapper">
        <div style={gridStyle} className="ag-theme-balham stock-grid-root">
          {isRowGroupPanelVisible && (
            <div className="stock-grid-group-actions">
              <button type="button" onClick={saveRowGroupOrder}>保存</button>
              <button type="button" onClick={resetRowGroupOrder}>リセット</button>
              <button type="button" onClick={closeRowGroupPanel}>閉じる</button>
            </div>
          )}
          <AgGridReact<StockRow>
            ref={gridRef}
            rowData={sortedDisplayData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            statusBar={isSelectionSummaryFooterVisible ? statusBar : undefined}
            components={components}
            autoGroupColumnDef={autoGroupColumnDef}
            isExternalFilterPresent={isExternalFilterPresent}
            doesExternalFilterPass={doesExternalFilterPass}
            onGridReady={handleGridReady}
            onColumnRowGroupChanged={handleColumnRowGroupChanged}
            onCellValueChanged={onCellValueChanged}
            onModelUpdated={handleModelUpdated}
            onSortChanged={handleSortChanged}
            localeText={localeText}
            loadingOverlayComponent={LoadingOverlay}
            getContextMenuItems={getContextMenuItems}
            rowGroupPanelShow={isRowGroupPanelVisible ? 'always' : 'never'}
            groupDisplayType="singleColumn"
            groupDefaultExpanded={DEFAULT_GROUP_EXPANDED_LEVEL}
            isGroupOpenByDefault={isGroupOpenByDefault}
            onRowGroupOpened={onRowGroupOpened}
            suppressAggFuncInHeader
            suppressScrollOnNewData
            rowHeight={36}
            cellSelection={{
              enableHeaderHighlight: true,
              handle: { mode: 'fill', direction: 'xy' }
            }}
            rowNumbers
            findSearchValue={externalFindSearchValue}
            onFindChanged={externalOnFindChanged}
            getRowClass={(params: { node: { level: number; group?: boolean } }) => {
              if (isTotalOnlyGroupNode(params.node as IRowNode<StockRow>)) {
                return undefined
              }
              if (params.node.group) {
                return `group-level-${params.node.level}`
              }
              return undefined
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default DataList
